import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global "Updating…" badge feed — any refresh=true call is a background
// recompute (served by a worker process); AppLayout listens to this event.
let _bgRefreshCount = 0;
const _emitBg = () => window.dispatchEvent(new CustomEvent("bg-refresh", { detail: { active: _bgRefreshCount > 0 } }));
axios.interceptors.request.use((config) => {
  if ((config.url || "").includes("refresh=true")) { config._bgRefresh = true; _bgRefreshCount++; _emitBg(); }
  return config;
});
axios.interceptors.response.use(
  (res) => {
    if (res.config?._bgRefresh) { _bgRefreshCount = Math.max(0, _bgRefreshCount - 1); _emitBg(); }
    const asOf = res.headers?.["x-data-as-of"];
    if (asOf) window.dispatchEvent(new CustomEvent("data-as-of", { detail: { ts: asOf } }));
    return res;
  },
  (err) => { if (err.config?._bgRefresh) { _bgRefreshCount = Math.max(0, _bgRefreshCount - 1); _emitBg(); } return Promise.reject(err); });

// Axios interceptor: auto-attach staff token + handle 401
// (never overwrite an explicit Authorization header — broker/portal pages set their own)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("stancor_token") || localStorage.getItem("stockreg_token");
  if (token && !config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Track consecutive 401s — only logout after auth/me fails
let _401count = 0;
const _clearAuthStorage = () => {
  ["stancor_token", "stancor_expires", "stancor_user", "stancor_perms"].forEach((k) => localStorage.removeItem(k));
};
axios.interceptors.response.use(
  (res) => { _401count = 0; return res; },
  (err) => {
    if (err.response?.status === 401) {
      const url = err.config?.url || "";
      // Only auto-logout if auth/me verification fails (confirms token is truly invalid)
      if (url.includes("/auth/me")) {
        _clearAuthStorage();
        window.location.reload();
      }
      // For other 401s, increment counter — after 3 consecutive, force logout
      _401count++;
      if (_401count >= 3 && !url.includes("/auth/login")) {
        _clearAuthStorage();
        window.location.reload();
      }
    }
    return Promise.reject(err);
  }
);

export const api = {
  getMonthlySummary: (dateFrom, dateTo, refresh = false) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/dispatch/monthly-summary?${p}`);
  },
  getDispatchData: (dateFrom, dateTo, refresh = false) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/dispatch/data?${p}`);
  },
  getDailyDrilldown: (month, refresh = false) => {
    const p = new URLSearchParams({ month });
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/dispatch/daily?${p}`);
  },
  getSizeSummary: (dateFrom, dateTo, refresh = false) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/dispatch/size-summary?${p}`);
  },
  getCustomers: (dateFrom, dateTo, refresh = false) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/customers?${p}`);
  },
  getCustomerDetail: (name, dateFrom, dateTo, refresh = false) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/customers/${encodeURIComponent(name)}/dispatches?${p}`);
  },
  getManufacturing: (dateFrom, dateTo, refresh = false) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/manufacturing/summary?${p}`);
  },
  getProductionVsDispatch: (dateFrom, dateTo, refresh = false) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/production-vs-dispatch?${p}`);
  },
  getStock: (refresh = false) => {
    const p = new URLSearchParams();
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/stock/summary?${p}`);
  },
  getFulfillment: (refresh = false) => {
    const p = new URLSearchParams();
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/fulfillment/tracker?${p}`);
  },
  getStockFilterOptions: () => axios.get(`${API}/stock/filter-options`),
  getStageFlow: (days = 30, refresh = false) => axios.get(`${API}/stock/stage-flow?days=${days}&refresh=${refresh}`),
  getStageFlowLots: (process, stage) => axios.get(`${API}/stock/stage-flow/lots?process=${encodeURIComponent(process)}&stage=${encodeURIComponent(stage)}`),
  getStageFlowMachines: (days = 30, refresh = false) => axios.get(`${API}/stock/stage-flow/machines?days=${days}&refresh=${refresh}`),
  setMachineDecommission: (name, decommissioned) => axios.post(`${API}/stock/stage-flow/machines/decommission`, { name, decommissioned }),
  getMachineDetail: (name, days = 30, refresh = false) => axios.get(`${API}/stock/stage-flow/machine-detail?name=${encodeURIComponent(name)}&days=${days}&refresh=${refresh}`),
  saveMachineSizeRange: (name, minNb, maxNb) => axios.post(`${API}/stock/stage-flow/machines/size-range`, { name, min_nb: minNb, max_nb: maxNb }),
  saveMachineSizePace: (name, nb, kgDay) => axios.post(`${API}/stock/stage-flow/machines/size-pace`, { name, nb, kg_day: kgDay }),
  saveMachineSizeRated: (name, nb, value, unit) => axios.post(`${API}/stock/stage-flow/machines/size-rated`, { name, nb, value, unit }),
  getSizePaceMatrix: (refresh = false) => axios.get(`${API}/stock/stage-flow/size-pace-matrix?refresh=${refresh}`),
  autofillSizePaceMatrix: (apply, llmCheck = true) => axios.post(`${API}/stock/stage-flow/size-pace-matrix/autofill`, { apply, llm_check: llmCheck }),
  getQualityOverview: () => axios.get(`${API}/quality/overview`),
  getQualityThroughput: () => axios.get(`${API}/quality/throughput`),
  getHeatFile: (lot) => axios.get(`${API}/quality/heat-file`, { params: { lot } }),
  bundleLookup: (code) => axios.get(`${API}/quality/bundle-lookup`, { params: { code } }),
  bundleDecision: (body) => axios.post(`${API}/quality/bundle-decision`, body),
  getDefectPareto: (days = 30) => axios.get(`${API}/quality/defects`, { params: { days } }),
  setQualityConfig: (payload) => axios.post(`${API}/quality/config`, payload),
  getQualitySODemand: (refresh = false) => axios.get(`${API}/quality/so-demand?refresh=${refresh}`),
  getHeatHistory: (lot) => axios.get(`${API}/quality/heat-history?lot=${encodeURIComponent(lot)}`),
  qcAction: (payload) => axios.post(`${API}/quality/qc-action`, payload),
  getQCDispositions: (limit = 100) => axios.get(`${API}/quality/dispositions?limit=${limit}`),
  reverseQCDisposition: (id) => axios.post(`${API}/quality/dispositions/${id}/reverse`),
  setJobworkConfig: (overdueDays) => axios.post(`${API}/jobwork/config`, { overdue_days: overdueDays }),
  getLoadRebalancer: (refresh = false) => axios.get(`${API}/stock/stage-flow/rebalancer?refresh=${refresh}`),
  getStockAnnotations: () => axios.get(`${API}/stock/annotations`),
  saveStockComment: (key, text) => axios.post(`${API}/stock/comments`, { key, text }),
  deleteStockComment: (key) => axios.delete(`${API}/stock/comments/${encodeURIComponent(key)}`),
  getStockAnnotationConfig: () => axios.get(`${API}/settings/stock-annotations-config`),
  setStockAnnotationConfig: (comment_writer_roles, downgrade_direct_roles, downgrade_request_roles) =>
    axios.post(`${API}/settings/stock-annotations-config`, { comment_writer_roles, downgrade_direct_roles, downgrade_request_roles }),
  // Grade B / Rework location setup + downgrade flow
  getGradeLocationsStatus: () => axios.get(`${API}/admin/grade-locations-status`),
  setupGradeLocations: () => axios.post(`${API}/admin/setup-grade-locations`, {}),
  cleanupReworkLocations: () => axios.post(`${API}/admin/cleanup-rework-locations`, {}),
  getDowngradeReasons: () => axios.get(`${API}/stock/downgrade-reasons`),
  submitDowngrade: (payload) => axios.post(`${API}/stock/downgrade`, payload),
  listDowngradeRequests: (status = "pending") => axios.get(`${API}/stock/downgrade-requests?status=${status}`),
  approveDowngradeRequest: (id, reason = "") => axios.post(`${API}/stock/downgrade-requests/${id}/approve`, { reason }),
  rejectDowngradeRequest: (id, reason = "") => axios.post(`${API}/stock/downgrade-requests/${id}/reject`, { reason }),
  listGradeMovements: (limit = 100, filters = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (filters.reason) params.set('reason', filters.reason);
    if (filters.to_grade) params.set('to_grade', filters.to_grade);
    return axios.get(`${API}/stock/grade-movements?${params.toString()}`);
  },
  reverseGradeMovement: (id, reason) => axios.post(`${API}/stock/grade-movements/${id}/reverse`, { reason }),
  getGradeBTrend: (weeks = 12) => axios.get(`${API}/dashboard/grade-b-trend?weeks=${weeks}`),
  getGradeBReasons: (days = 30) => axios.get(`${API}/dashboard/grade-b-reasons?days=${days}`),
  // Dashboard Phase A — Money, Production Output, Monthly Targets
  getMoneyTile: (window = "month") =>
    axios.get(`${API}/dashboard/money-tile?window=${encodeURIComponent(window)}`),
  getProductionOutput: (window = "today") =>
    axios.get(`${API}/dashboard/production-today?window=${encodeURIComponent(window)}`),
  getMonthlyTargets: (year, month) =>
    axios.get(`${API}/settings/monthly-targets?year=${year}&month=${month}`),
  saveMonthlyTarget: (payload) =>
    axios.post(`${API}/settings/monthly-targets`, payload),
  // Dashboard Phase C — KPI deltas, Bottleneck config, Top-N pending SOs
  getKpiDeltas: (window = "month") =>
    axios.get(`${API}/dashboard/kpi-deltas?window=${encodeURIComponent(window)}`),
  getBottleneckConfig: () => axios.get(`${API}/settings/bottleneck-config`),
  saveBottleneckConfig: (payload) => axios.post(`${API}/settings/bottleneck-config`, payload),
  getStageCapacity: () => axios.get(`${API}/settings/stage-capacity`),
  saveStageCapacity: (key, kgPerDay) => axios.post(`${API}/settings/stage-capacity`, { key, kg_per_day: kgPerDay }),
  getTopPendingSOs: (limit = 5, refresh = false) => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/dashboard/top-pending-sos?${p.toString()}`);
  },
  // Planning MO-state filter + Stale MOs housekeeping
  listStaleMOs: (days = 30, limit = 500) =>
    axios.get(`${API}/settings/stale-mos?days=${days}&limit=${limit}`),
  previewStaleMOCancel: (payload) =>
    axios.post(`${API}/settings/stale-mos/preview`, payload),
  cancelStaleMOs: (payload) =>
    axios.post(`${API}/settings/stale-mos/cancel`, payload),
  // Q2d — SO Grade-A-only flag + pre-check
  getSOFlags: (soNumber) =>
    axios.get(`${API}/sales/so-flags/${encodeURI(soNumber)}`),
  setSOFlags: (soNumber, gradeAOnly) =>
    axios.post(`${API}/sales/so-flags`, { so_number: soNumber, grade_a_only: gradeAOnly }),
  checkGradeAOnly: (soNumber) =>
    axios.get(`${API}/sales/grade-a-check/${encodeURI(soNumber)}`),
  // Q4i — Reserve / Unreserve stock for an SO's outgoing pickings
  reserveSOStock: (soNumber) =>
    axios.post(`${API}/sales/reserve`, { so_number: soNumber }),
  unreserveSOStock: (soNumber) =>
    axios.post(`${API}/sales/unreserve`, { so_number: soNumber }),
  getCustomerSuggestions: (months = 12, refresh = false) => {
    const p = new URLSearchParams();
    p.append("months", String(months));
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/stock/customer-suggestions?${p}`);
  },
  getPurchase: (refresh = false) => {
    const p = new URLSearchParams();
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/purchase/data?${p}`);
  },
  exportPurchase: () => axios.post(`${API}/purchase/export`, {}, { responseType: "blob" }),
  getJobWork: (months = 6, refresh = false) => {
    const p = new URLSearchParams();
    p.append("months", String(months));
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/jobwork/data?${p}`);
  },
  searchSalesOrders: (q = "", limit = 20) => {
    const p = new URLSearchParams();
    p.append("q", q);
    p.append("limit", String(limit));
    return axios.get(`${API}/jobwork/so-search?${p}`);
  },
  linkJobWorkSO: (picking_ref, so_id, so_name) => {
    return axios.post(`${API}/jobwork/link-so`, { picking_ref, so_id, so_name });
  },
  unlinkJobWorkSO: (picking_ref) => {
    return axios.delete(`${API}/jobwork/link-so/${encodeURIComponent(picking_ref)}`);
  },
  customStockExport: (config) => {
    return axios.post(`${API}/stock/custom-export`, config, { responseType: "blob" });
  },
  exportStockView: (params) => {
    return axios.post(`${API}/stock/export-view`, params, { responseType: "blob" });
  },
  exportStockPivotExcel: (params) => {
    return axios.post(`${API}/stock/export-pivot-excel`, params, { responseType: "blob" });
  },
  exportFulfillmentView: (params) => {
    return axios.post(`${API}/fulfillment/export-view`, params, { responseType: "blob" });
  },
  exportExcelUrl: (dateFrom, dateTo) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    return `${API}/dispatch/export?${p}`;
  },
  exportPdfUrl: (dateFrom, dateTo) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    return `${API}/dispatch/export-pdf?${p}`;
  },
  healthCheck: () => axios.get(`${API}/health`),

  // Chat
  chatSend: (message, sessionId) => axios.post(`${API}/chat/send`, { message, session_id: sessionId }, { timeout: 30000 }),
  chatJob: (jobId) => axios.get(`${API}/chat/job/${jobId}`, { timeout: 20000 }),
  chatReportDownload: (reportId, fmt) => axios.get(`${API}/chat/report/${reportId}/download?fmt=${fmt}`, { responseType: "blob", timeout: 120000 }),
  chatExport: (message, sessionId) => axios.post(`${API}/chat/export`, { message, session_id: sessionId }, { responseType: "blob" }),
  chatHistory: (sessionId) => axios.get(`${API}/chat/history/${sessionId}`),
  chatSessions: () => axios.get(`${API}/chat/sessions`),
  chatSaveMemory: (text) => axios.post(`${API}/chat/memory`, { text }),
  chatSuggest: (q) => axios.get(`${API}/chat/suggest?q=${encodeURIComponent(q)}`, { timeout: 5000 }),
  getChatAliases: () => axios.get(`${API}/chat/aliases`),
  addChatAlias: (short, full) => axios.post(`${API}/chat/aliases`, { short, full }),
  deleteChatAlias: (short) => axios.delete(`${API}/chat/aliases/${encodeURIComponent(short)}`),
  chatSchedules: () => axios.get(`${API}/chat/schedules`),
  chatScheduleCreate: (payload) => axios.post(`${API}/chat/schedules`, payload),
  chatScheduleToggle: (id) => axios.put(`${API}/chat/schedules/${id}/toggle`),
  chatScheduleDelete: (id) => axios.delete(`${API}/chat/schedules/${id}`),
  chatScheduleRunNow: (id) => axios.post(`${API}/chat/schedules/${id}/run-now`),
  // Planning Dashboard
  getPlanningGap: (refresh = false, includeDraft = false) => {
    const p = new URLSearchParams();
    if (refresh) p.append("refresh", "true");
    if (includeDraft) p.append("include_draft", "true");
    return axios.get(`${API}/planning/gap?${p}`);
  },
  getPlanningPlan: (monthsCsv) => axios.get(`${API}/planning/plan?month=${encodeURIComponent(monthsCsv)}`),
  putPlanningPlan: (payload) => axios.put(`${API}/planning/plan`, payload),
  deletePlanningPlan: (month, key, reason = "") => axios.delete(`${API}/planning/plan?month=${encodeURIComponent(month)}&key=${encodeURIComponent(key)}&reason=${encodeURIComponent(reason)}`),
  addPlanComment: (payload) => axios.post(`${API}/planning/plan/comment`, payload),
  reorderPlan: (payload) => axios.post(`${API}/planning/plan/reorder`, payload),
  getSOCoverage: () => axios.get(`${API}/planning/so-coverage`),
  setSOPriority: (payload) => axios.post(`${API}/planning/so-priority`, payload),
  getPlanLock: (month) => axios.get(`${API}/planning/lock?month=${month}`),
  setPlanLock: (payload) => axios.post(`${API}/planning/lock`, payload),
  getPlanAudit: (month) => axios.get(`${API}/planning/audit?month=${month}`),
  getPlanVsActual: (month, refresh = false) => axios.get(`${API}/planning/plan-vs-actual?month=${month}${refresh ? "&refresh=true" : ""}`),
  carryForwardPlan: (payload) => axios.post(`${API}/planning/plan/carry-forward`, payload),
  procurementExport: (includeDraft = false) =>
    axios.get(`${API}/planning/procurement-export${includeDraft ? "?include_draft=true" : ""}`, { responseType: "blob" }),
  getPlanSheet: (month, version = null, compare = false) => {
    const p = new URLSearchParams({ month });
    if (version) p.append("version", version);
    if (compare) p.append("compare", "true");
    return axios.get(`${API}/planning/plan-sheet?${p}`);
  },
  planSheetExport: (month, version = null) => {
    const p = new URLSearchParams({ month });
    if (version) p.append("version", version);
    return axios.get(`${API}/planning/plan-sheet-export?${p}`, { responseType: "blob" });
  },
  getPlanningCapacity: (refresh = false) => axios.get(`${API}/planning/capacity${refresh ? "?refresh=true" : ""}`),
  getIncomingPOs: (refresh = false) => axios.get(`${API}/planning/incoming-pos${refresh ? "?refresh=true" : ""}`),
  stalePOExport: (minDays = 90) => axios.get(`${API}/planning/stale-pos-export?min_days=${minDays}`, { responseType: "blob" }),
  getPlanningForecast: (refresh = false) => axios.get(`${API}/planning/forecast${refresh ? "?refresh=true" : ""}`),
  getReorderAlerts: (refresh = false) => axios.get(`${API}/sales/reorder-alerts${refresh ? "?refresh=true" : ""}`),
  // Offers & Pricing
  getOfferDraftStock: (refresh = false) => axios.get(`${API}/offers/draft-stock${refresh ? "?refresh=true" : ""}`),
  getDraftWizardData: (refresh = false) => axios.get(`${API}/offers/draft-wizard-data`, { params: { refresh }, timeout: 180000 }),
  offersStockSearch: (q) => axios.get(`${API}/offers/stock-search?q=${encodeURIComponent(q)}`),
  getDeadStock: (days = 180, refresh = false, scope = "sellable") => axios.get(`${API}/offers/dead-stock?min_days=${days}&scope=${scope}${refresh ? "&refresh=true" : ""}`, { timeout: 180000 }),
  getDeadStockAlert: (days = 90) => axios.get(`${API}/offers/dead-stock/alert?min_days=${days}`, { timeout: 180000 }),
  getDeadStockDiscountCfg: () => axios.get(`${API}/offers/dead-stock/discount-config`),
  saveDeadStockDiscountCfg: (cfg) => axios.post(`${API}/offers/dead-stock/discount-config`, cfg),
  getDeadStockLiquidation: (days, scope = "sellable") => axios.get(`${API}/offers/dead-stock/liquidation?min_days=${days}&scope=${scope}`, { timeout: 180000 }),
  exportDeadStockLiquidation: (days, scope = "sellable", extra = {}) => axios.get(`${API}/offers/dead-stock/liquidation/export`, { params: { min_days: days, scope, ...extra }, responseType: "blob", timeout: 180000 }),
  getDeadStockTrend: (days, scope = "sellable", refresh = false) => axios.get(`${API}/offers/dead-stock/trend?min_days=${days}&scope=${scope}${refresh ? "&refresh=true" : ""}`, { timeout: 280000 }),
  getOrderBook: (refresh = false) => axios.get(`${API}/sales/order-book${refresh ? "?refresh=true" : ""}`, { timeout: 180000 }),
  exportOrderBook: (mode = "open") => axios.get(`${API}/sales/order-book/export?mode=${mode}`, { responseType: "blob", timeout: 180000 }),
  exportOrderBookSO: (mode = "open", customers = "", details = 0) => axios.get(`${API}/sales/order-book/export-so?mode=${mode}&customers=${encodeURIComponent(customers)}&details=${details}`, { responseType: "blob", timeout: 180000 }),
  getOrderBookLeadTimes: () => axios.get(`${API}/sales/order-book/lead-times`, { timeout: 120000 }),
  getOrderBookStageStock: () => axios.get(`${API}/sales/order-book/stage-stock`),
  getQtyExceptions: (params) => axios.get(`${API}/qty-guard/exceptions`, { params }),
  scanQtyGuard: (body) => axios.post(`${API}/qty-guard/scan`, body, { timeout: 300000 }),
  dismissQtyExceptions: (ids, note) => axios.post(`${API}/qty-guard/dismiss`, { ids, note }),
  reopenQtyExceptions: (ids) => axios.post(`${API}/qty-guard/reopen`, { ids }),
  getQtySoBadges: () => axios.get(`${API}/qty-guard/so-badges`),
  getStalePos: (params) => axios.get(`${API}/purchase/stale-pos`, { params, timeout: 180000 }),
  getSizeMaster: (params) => axios.get(`${API}/catalog/size-master`, { params, timeout: 300000 }),
  getWpmCheck: (params) => axios.get(`${API}/catalog/wpm-check`, { params, timeout: 300000 }),
  pushWpmCheck: (payload) => axios.post(`${API}/catalog/wpm-check/push`, payload, { timeout: 300000 }),
  exportWpmCheck: () => axios.get(`${API}/catalog/wpm-check/export`, { responseType: "blob", timeout: 300000 }),
  tickStalePo: (po, done, note) => axios.post(`${API}/purchase/stale-pos/tick`, { po, done, note }),
  getOrderBookSoDetail: (so) => axios.get(`${API}/sales/order-book/so-detail?so=${encodeURIComponent(so)}`, { timeout: 60000 }),
  getOrderBookStaleMos: (days = 30) => axios.get(`${API}/sales/order-book/stale-mos?days=${days}`, { timeout: 90000 }),
  getMoCoverage: (refresh = false) => axios.get(`${API}/sales/mo-coverage${refresh ? "?refresh=true" : ""}`, { timeout: 120000 }),
  getMoCoverageExport: () => axios.get(`${API}/sales/mo-coverage/export`, { responseType: "blob", timeout: 120000 }),
  getDeliveryRisks: () => axios.get(`${API}/sales/delivery-risks`, { timeout: 120000 }),
  exportPromiseReport: () => axios.get(`${API}/sales/order-book/promise-report`, { responseType: "blob", timeout: 180000 }),
  getPromiseRevisions: () => axios.get(`${API}/sales/promise-revisions`),
  getOrderBookAlert: () => axios.get(`${API}/sales/order-book/alert`, { timeout: 180000 }),
  getOrderBookTrend: (weeks = 12, refresh = false) => axios.get(`${API}/sales/order-book/trend?weeks=${weeks}${refresh ? "&refresh=true" : ""}`, { timeout: 280000 }),
  getOrderBookMoStatus: (refresh = false) => axios.get(`${API}/sales/order-book/mo-status${refresh ? "?refresh=true" : ""}`, { timeout: 180000 }),
  getOrderBookMachineLoad: (refresh = false) => axios.get(`${API}/sales/order-book/machine-load${refresh ? "?refresh=true" : ""}`, { timeout: 180000 }),
  getFilterViews: (page) => axios.get(`${API}/settings/filter-views?page=${page}`),
  getPlanLinks: (month = "") => axios.get(`${API}/planning/plan-links${month ? `?month=${month}` : ""}`, { timeout: 280000 }),
  getReservationAlignment: (refresh = false) => axios.get(`${API}/planning/reservation-alignment${refresh ? "?refresh=true" : ""}`, { timeout: 280000 }),
  getJobs: (refresh = false) => axios.get(`${API}/jobs${refresh ? "?refresh=true" : ""}`, { timeout: 280000 }),
  getJobsMap: () => axios.get(`${API}/jobs/map`, { timeout: 280000 }),
  // Data Fix Center (writes to LIVE Odoo)
  getSoDateLines: () => axios.get(`${API}/odoo-fix/so-lines`, { timeout: 120000 }),
  pushSoDates: (payload) => axios.post(`${API}/odoo-fix/so-lines/push`, payload, { timeout: 120000 }),
  getMoReview: () => axios.get(`${API}/odoo-fix/mo-review`, { timeout: 120000 }),
  pushMoActions: (actions) => axios.post(`${API}/odoo-fix/mo-push`, { actions }, { timeout: 180000 }),
  moCancelPreview: (mo_ids) => axios.post(`${API}/odoo-fix/mo-cancel-preview`, { mo_ids }, { timeout: 120000 }),
  getFixAudit: () => axios.get(`${API}/odoo-fix/audit`),
  revertOdooEdit: (entry_id) => axios.post(`${API}/odoo-fix/audit/revert`, { entry_id }),
  // Odoo Setup Assistant
  getBomAudit: () => axios.get(`${API}/odoo-setup/bom-audit`, { timeout: 180000 }),
  addByproducts: (items) => axios.post(`${API}/odoo-setup/byproducts/add`, { items }, { timeout: 180000 }),
  getSetupWorkcenters: () => axios.get(`${API}/odoo-setup/workcenters`, { timeout: 60000 }),
  createWorkcenters: (names) => axios.post(`${API}/odoo-setup/workcenters/create`, { names }, { timeout: 120000 }),
  getScrapProducts: () => axios.get(`${API}/odoo-setup/scrap-products`, { timeout: 60000 }),
  createScrapProducts: (names, template_product_id) => axios.post(`${API}/odoo-setup/scrap-products/create`, { names, template_product_id }, { timeout: 120000 }),
  getOdooCalendars: () => axios.get(`${API}/odoo-setup/calendars`, { timeout: 60000 }),
  createOdooCalendar: (payload) => axios.post(`${API}/odoo-setup/calendars/create`, payload, { timeout: 120000 }),
  assignOdooCalendar: (payload) => axios.post(`${API}/odoo-setup/calendars/assign`, payload, { timeout: 120000 }),
  addOdooCalendarLeave: (payload) => axios.post(`${API}/odoo-setup/calendars/leave`, payload, { timeout: 120000 }),
  getSetupLog: () => axios.get(`${API}/odoo-setup/log`),
  undoSetupAction: (log_id) => axios.post(`${API}/odoo-setup/log/${log_id}/undo`, {}, { timeout: 120000 }),
  // Week Plan Engine
  getWeekGrid: (params = {}) => axios.get(`${API}/weekplan/grid`, { params, timeout: 280000 }),
  getDieCheck: () => axios.get(`${API}/weekplan/die-check`, { timeout: 280000 }),
  getHeatDocs: (heat) => axios.get(`${API}/heats/${encodeURIComponent(heat)}/docs`),
  uploadHeatDoc: (heat, formData) => axios.post(`${API}/heats/${encodeURIComponent(heat)}/docs`, formData, { timeout: 120000 }),
  downloadHeatDoc: (id) => axios.get(`${API}/heat-docs/${id}/download`, { responseType: "blob", timeout: 60000 }),
  deleteHeatDoc: (id) => axios.delete(`${API}/heat-docs/${id}`),
  getMtcCompany: () => axios.get(`${API}/mtc/company`),
  getMtcFormats: (customer = "") => axios.get(`${API}/mtc/formats`, { params: customer ? { customer } : {} }),
  getTypicalPolicy: () => axios.get(`${API}/mtc/typical-policy`),
  putTypicalPolicy: (body) => axios.put(`${API}/mtc/typical-policy`, body),
  getQuickLines: () => axios.get(`${API}/mtc/quick-lines`),
  addQuickLine: (body) => axios.post(`${API}/mtc/quick-lines`, body),
  removeQuickLine: (pid) => axios.delete(`${API}/mtc/quick-lines/${pid}`),
  getMtcDispatches: (q = "", opts = {}) => axios.get(`${API}/mtc/dispatches`, { params: { q, limit: opts.limit || 120, date_from: opts.date_from || "", date_to: opts.date_to || "", show_hidden: !!opts.show_hidden }, timeout: 60000 }),
  bulkIgnoreDispatches: (ids, reason) => axios.post(`${API}/mtc/cert-gaps/bulk-ignore`, { ids, reason }),
  unignoreDispatches: (ids) => axios.post(`${API}/mtc/cert-gaps/unignore`, { ids }),
  getLocalUsers: () => axios.get(`${API}/settings/local-users`),
  createLocalUser: (body) => axios.post(`${API}/settings/local-users`, body),
  resetLocalUserPassword: (username, password) => axios.post(`${API}/settings/local-users/${encodeURIComponent(username)}/password`, { password }),
  deleteLocalUser: (username) => axios.delete(`${API}/settings/local-users/${encodeURIComponent(username)}`),
  setHeatOrigin: (heat, origin) => axios.put(`${API}/mtc/heat-tests/${encodeURIComponent(heat)}/origin`, { origin }),
  getHeatLots: (heat) => axios.get(`${API}/mtc/heat-tests/${encodeURIComponent(heat)}/lots`),
  saveHeatLots: (heat, lots) => axios.put(`${API}/mtc/heat-tests/${encodeURIComponent(heat)}/lots`, { lots }),
  getMtcDrafts: () => axios.get(`${API}/mtc/drafts`),
  getMtcPeople: () => axios.get(`${API}/mtc/people`),
  ensureHeat: (heat, note = "") => axios.post(`${API}/mtc/heats/ensure`, { heat, note }),
  getHeatStock: (pickingId, productId) => axios.get(`${API}/mtc/heat-stock`, { params: { picking_id: pickingId, product_id: productId }, timeout: 45000 }),
  setHeatOverride: (body) => axios.post(`${API}/mtc/heat-override`, body),
  getHeatOverrides: () => axios.get(`${API}/mtc/heat-overrides`),
  closeHeatOverride: (id) => axios.delete(`${API}/mtc/heat-overrides/${id}`),
  assignMtcDraft: (id, to, note) => axios.put(`${API}/mtc/drafts/${id}/assign`, { to, note }),
  getCertActualQty: (certNo) => axios.get(`${API}/mtc/certs/${certNo}/actual-qty`, { timeout: 60000 }),
  getMtcDraft: (id) => axios.get(`${API}/mtc/drafts/${id}`),
  saveMtcDraft: (body) => axios.put(`${API}/mtc/drafts`, body, { timeout: 20000 }),
  deleteMtcDraft: (id) => axios.delete(`${API}/mtc/drafts/${id}`),
  keepMtcDraft: (id, reason) => axios.put(`${API}/mtc/drafts/${id}/keep`, { reason }),
  refreshDraftFromOdoo: (id, apply = false) => axios.post(`${API}/mtc/drafts/${id}/odoo-refresh`, { apply }, { timeout: 60000 }),
  claimMtcDraft: (id) => axios.post(`${API}/mtc/drafts/${id}/claim`, {}),
  takeoverMtcDraft: (id, reason) => axios.post(`${API}/mtc/drafts/${id}/takeover`, { reason }),
  releaseMtcDraft: (id) => axios.post(`${API}/mtc/drafts/${id}/release`, {}),
  takeoverSeenMtcDraft: (id) => axios.post(`${API}/mtc/drafts/${id}/takeover-seen`, {}),
  getMtcSizeSummary: (date_from = "", date_to = "") => axios.get(`${API}/mtc/size-summary`, { params: { date_from, date_to }, timeout: 60000 }),
  getMtcHeatSummary: (p = {}) => axios.get(`${API}/mtc/heat-summary`, { params: { date_from: p.from || "", date_to: p.to || "", heats: p.heats || "" }, timeout: 120000 }),
  downloadMtcHeatSummary: async (date_from = "", date_to = "") => {
    const r = await axios.get(`${API}/mtc/heat-summary.xlsx`, { params: { date_from, date_to }, responseType: "blob", timeout: 120000 });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `MTC-by-heat-${date_from || "all"}.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
  },
  getMtcPendingItems: (p = {}) => axios.get(`${API}/mtc/pending-items`, { params: { date_from: p.from || "", date_to: p.to || "", q: p.q || "", only: p.only || "pending" }, timeout: 90000 }),
  downloadMtcPendingItems: async (p = {}) => {
    const r = await axios.get(`${API}/mtc/pending-items.xlsx`, { params: { date_from: p.from || "", date_to: p.to || "", q: p.q || "", only: p.only || "pending" }, responseType: "blob", timeout: 120000 });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `MTC-pending-items-${p.from || "latest"}.xlsx`;
    a.click();
  },
  getInspectionCerts: (iid) => axios.get(`${API}/mtc/inspection-certs/${iid}`),
  getQapMeta: () => axios.get(`${API}/qap/meta`),
  searchQapSos: (q = "") => axios.get(`${API}/qap/so-search`, { params: { q }, timeout: 60000 }),
  getOrderQaps: (q = "") => axios.get(`${API}/qap/orders`, { params: { q } }),
  getOrderQap: (id) => axios.get(`${API}/qap/orders/${id}`, { timeout: 60000 }),
  attachOrderQap: (body) => axios.post(`${API}/qap/orders`, body, { timeout: 60000 }),
  detachOrderQap: (id) => axios.delete(`${API}/qap/orders/${id}`),
  openOrderQapPack: async (id) => {
    const r = await axios.get(`${API}/qap/orders/${id}/pack.pdf`, { responseType: "blob", timeout: 120000 });
    window.open(URL.createObjectURL(r.data), "_blank");
  },
  getQapTemplates: (q = "") => axios.get(`${API}/qap/templates`, { params: { q } }),
  getQapTemplate: (id) => axios.get(`${API}/qap/templates/${id}`),
  createQapTemplate: (body) => axios.post(`${API}/qap/templates`, body),
  updateQapTemplate: (id, body) => axios.put(`${API}/qap/templates/${id}`, body),
  cloneQapTemplate: (id, body) => axios.post(`${API}/qap/templates/${id}/clone`, body),
  deleteQapTemplate: (id) => axios.delete(`${API}/qap/templates/${id}`),
  openQapPdf: async (id) => {
    const r = await axios.get(`${API}/qap/templates/${id}/pdf`, { responseType: "blob", timeout: 90000 });
    window.open(URL.createObjectURL(r.data), "_blank");
  },
  requestTpiCallSign: (body) => axios.post(`${API}/mtc/tpi-call-sign`, { ...body, origin: window.location.origin }),
  downloadMtcSizeSummary: async (date_from = "", date_to = "") => {
    const r = await axios.get(`${API}/mtc/size-summary.xlsx`, { params: { date_from, date_to }, responseType: "blob", timeout: 90000 });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `MTC-by-size-${date_from || "all"}-${date_to || "today"}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  // Daily Production Report
  getDpr: (date, team) => axios.get(`${API}/dpr`, { params: { date, team }, timeout: 90000 }),
  getDprPeriod: (from, to, team) => axios.get(`${API}/dpr/period`, { params: { date_from: from, date_to: to, team }, timeout: 120000 }),
  getDprTeams: () => axios.get(`${API}/dpr/teams`),
  saveDprStageTeam: (stage, team) => axios.put(`${API}/dpr/teams`, { stage, team }),
  getDprTargets: () => axios.get(`${API}/dpr/targets`),
  saveDprTarget: (body) => axios.put(`${API}/dpr/targets`, body),
  saveDprHolidays: (holidays) => axios.put(`${API}/dpr/holidays`, { holidays }),
  saveDprStageMachines: (stage, machines) => axios.put(`${API}/dpr/stage-machines`, { stage, machines }),
  setDprTargetOverride: (body) => axios.post(`${API}/dpr/target-override`, body),
  getDprPlan: (date) => axios.get(`${API}/dpr/plan`, { params: { date } }),
  getDprWeekPlan: (date) => axios.get(`${API}/dpr/week-plan`, { params: { date }, timeout: 180000 }),
  saveDprWeekPlan: (body) => axios.put(`${API}/dpr/week-plan`, body),
  getDprWeekFromTargets: (date) => axios.get(`${API}/dpr/week-plan/targets`, { params: { date } }),
  getOperators: () => axios.get(`${API}/settings/operators`),
  addOperator: (name, dept = "") => axios.post(`${API}/settings/operators`, { name, dept }),
  patchOperator: (oid, body) => axios.patch(`${API}/settings/operators/${oid}`, body),
  saveDprPlan: (body) => axios.put(`${API}/dpr/plan`, body),
  prefillDprPlan: (date, source) => axios.post(`${API}/dpr/plan/prefill`, { date, source }, { timeout: 60000 }),
  addDprVariance: (body) => axios.post(`${API}/dpr/variance`, body),
  removeDprVariance: (body) => axios.post(`${API}/dpr/variance/remove`, body),
  getDprVarianceSummary: (from, to, team) => axios.get(`${API}/dpr/variance/summary`, { params: { date_from: from, date_to: to, team } }),
  getDprPdf: (date, team) => axios.get(`${API}/dpr/pdf`, { params: { date, team }, responseType: "blob", timeout: 60000 }),
  getDprPeriodPdf: (from, to, team, title) => axios.get(`${API}/dpr/period/pdf`, { params: { date_from: from, date_to: to, team, title }, responseType: "blob", timeout: 120000 }),
  getDprConfig: () => axios.get(`${API}/settings/dpr-config`),
  saveDprConfig: (data) => axios.put(`${API}/settings/dpr-config`, data),
  getReminderConfig: () => axios.get(`${API}/reminders/config`),
  saveReminderConfig: (data) => axios.put(`${API}/reminders/config`, data),
  testReminderTelegram: () => axios.post(`${API}/reminders/test`),
  runReminderNow: (data) => axios.post(`${API}/reminders/run-now`, data || {}),
  getReminderLog: () => axios.get(`${API}/reminders/log`),
  reassignDprMos: (data) => axios.post(`${API}/dpr/reassign`, data),
  undoDprReassign: (moId) => axios.delete(`${API}/dpr/reassign/${moId}`),
  getMtcDispatchDetail: (id) => axios.get(`${API}/mtc/dispatches/${id}`, { timeout: 60000 }),
  getMtcSoList: (q = "") => axios.get(`${API}/mtc/so-list`, { params: { q }, timeout: 60000 }),
  getMtcSoDetail: (id) => axios.get(`${API}/mtc/so/${id}`, { timeout: 60000 }),
  attachMtcDispatch: (certNo, picking) => axios.post(`${API}/mtc/certs/${encodeURIComponent(certNo)}/attach-dispatch`, { picking }, { timeout: 60000 }),
  getHeatTests: (params = {}) => axios.get(`${API}/mtc/heat-tests`, { params }),
  saveHeatTest: (heat, body) => axios.post(`${API}/mtc/heat-tests/${encodeURIComponent(heat)}`, body),
  suggestHeatTest: (heat) => axios.get(`${API}/mtc/heat-tests/${encodeURIComponent(heat)}/suggest`),
  createMtcCert: (body) => axios.post(`${API}/mtc/certs`, body),
  linkMtcSheets: (certNos) => axios.post(`${API}/mtc/certs/link-sheets`, { cert_nos: certNos }),
  aiPrecheckMtc: (body) => axios.post(`${API}/mtc/ai-precheck`, body),
  getInspections: (status = "", q = "") => axios.get(`${API}/inspections`, { params: { status, q } }),
  createInspection: (body) => axios.post(`${API}/inspections`, body, { timeout: 60000 }),
  updateInspection: (id, body) => axios.put(`${API}/inspections/${id}`, body),
  deleteInspection: (id) => axios.delete(`${API}/inspections/${id}`),
  checkInspection: (soNo) => axios.get(`${API}/inspections-check`, { params: { so_no: soNo } }),
  aiScanInspections: () => axios.post(`${API}/inspections/ai-scan`, {}, { timeout: 180000 }),
  getInspectionSettings: () => axios.get(`${API}/inspections/settings`),
  saveInspectionSettings: (body) => axios.put(`${API}/inspections/settings`, body),
  openInspectionCallLetter: async (id) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.get(`${API}/mtc/pdf-token`);
      const url = `${API}/inspections/${id}/call-letter?t=${data.t}`;
      if (w) w.location = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); throw e; }
  },
  getInspectionPack: async (id) => {
    const w = window.open("", "_blank");
    try {
      const r = await axios.get(`${API}/inspections/${id}/inspection-pack`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (w) w.location = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); throw e; }
  },
  getMtcCerts: (q = "") => axios.get(`${API}/mtc/certs`, { params: { q } }),
  revokeMtcCert: (certNo, reason) => axios.post(`${API}/mtc/certs/${certNo}/revoke`, { reason }),
  reviseMtcCert: (certNo, note, updates = null, customerNote = "", qtyTrued = false, heatReason = "") => axios.post(`${API}/mtc/certs/${certNo}/revise`, { note, customer_note: customerNote, ...(heatReason ? { heat_reason: heatReason } : {}), ...(qtyTrued ? { qty_trued: true } : {}), ...(updates ? { updates } : {}) }),
  getMtcMyRights: () => axios.get(`${API}/mtc/my-rights`),
  getMtcApprovalConfig: () => axios.get(`${API}/mtc/approval-config`),
  saveMtcApprovalConfig: (body) => axios.put(`${API}/mtc/approval-config`, body),
  getMtcApprovals: (status = "pending") => axios.get(`${API}/mtc/approvals`, { params: { status } }),
  decideMtcApproval: (id, decision, note = "") => axios.post(`${API}/mtc/approvals/${id}/decide`, { decision, note }),
  withdrawMtcApproval: (id) => axios.post(`${API}/mtc/approvals/${id}/withdraw`),
  getMtcWatchlist: () => axios.get(`${API}/mtc/watchlist`),
  ackMtcFlag: (flagId, note) => axios.post(`${API}/mtc/watchlist/ack`, { flag_id: flagId, note }),
  unackMtcFlag: (flagId) => axios.delete(`${API}/mtc/watchlist/ack/${flagId}`),
  getMtcWatchConfig: () => axios.get(`${API}/mtc/watch-config`),
  saveMtcWatchConfig: (body) => axios.put(`${API}/mtc/watch-config`, body),
  getMtcRights: () => axios.get(`${API}/mtc/rights`),
  saveMtcRights: (body) => axios.put(`${API}/mtc/rights`, body),
  getMtcAudit: (params) => axios.get(`${API}/mtc/audit`, { params }),
  getMtcRevisions: (params) => axios.get(`${API}/mtc/revisions`, { params }),
  getMtcReissueReasons: () => axios.get(`${API}/mtc/reissue-reasons`),
  getTvScreens: () => axios.get(`${API}/tv/screens`),
  addTvScreen: (body) => axios.post(`${API}/tv/screens`, body),
  patchTvScreen: (id, body) => axios.patch(`${API}/tv/screens/${id}`, body),
  deleteTvScreen: (id) => axios.delete(`${API}/tv/screens/${id}`),
  getMtcCertPdf: (certNo, copy = 0) => axios.get(`${API}/mtc/certs/${certNo}/pdf`, { params: { base: window.location.origin, ...(copy ? { copy: 1 } : {}) }, responseType: "blob", timeout: 120000 }),
  getTdsPdfBlob: (grade) => axios.get(`${API}/mtc/tds/${encodeURIComponent(grade)}/pdf`, { responseType: "blob", timeout: 120000 }),
  getTdcPdfBlob: (no) => axios.get(`${API}/mtc/tdc/${no}/pdf`, { responseType: "blob", timeout: 120000 }),
  getCallLetterBlob: (id) => axios.get(`${API}/inspections/${id}/call-letter`, { responseType: "blob", timeout: 120000 }),
  getInquiryRouting: () => axios.get(`${API}/inquiry-routing`),
  saveInquiryRouting: (body) => axios.put(`${API}/inquiry-routing`, body),
  assignInquiry: (iid, deptId, email) => axios.post(`${API}/inquiries/${iid}/assign`, { dept_id: deptId, email }),
  replyInquiry: (iid, body) => axios.post(`${API}/inquiries/${iid}/reply`, body),
  mtcPdfCheck: (certNo, copy = 0) => axios.get(`${API}/mtc/certs/${certNo}/pdf-check`, { params: copy ? { copy: 1 } : {} }),
  openMtcCertPdf: async (certNo, copy = 0, review = 0) => {
    // The tab is opened on the click (a pop-up blocker kills a window.open after an await), and it is
    // given something to look at at once — a certificate takes a few seconds to render and an empty tab
    // reads as a failure. The file itself is then streamed from the server, not handed over as a blob,
    // so the browser shows its own progress and saves it under the name the server gives it.
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(`<!doctype html><title>${certNo} — preparing</title>
        <style>body{margin:0;height:100vh;display:flex;align-items:center;justify-content:center;
        font:14px/1.6 system-ui,sans-serif;background:#1B2A4A;color:#fff}
        .b{text-align:center}.s{width:26px;height:26px;margin:0 auto 14px;border:3px solid rgba(255,255,255,.25);
        border-top-color:#fff;border-radius:50%;animation:r .8s linear infinite}
        @keyframes r{to{transform:rotate(360deg)}}small{opacity:.6}</style>
        <div class=b><div class=s></div><b>Preparing ${certNo}</b><br><small id=m>building the certificate, signing and securing it — a few seconds</small></div>
        <script>setTimeout(function(){var m=document.getElementById('m');if(m){m.innerHTML='If the certificate has saved to your downloads instead of opening here, you can close this tab.';}},18000)<\/script>`);
      w.document.close();
    }
    try {
      const chk = (await axios.get(`${API}/mtc/certs/${certNo}/pdf-check`, { params: copy ? { copy: 1 } : {} })).data;
      if (!chk.ok) {
        if (w) w.close();
        const err = new Error(chk.detail || "PDF refused");
        err.serverMessage = chk.detail;
        throw err;
      }
      const t = (await axios.get(`${API}/mtc/pdf-token`)).data.t;
      const url = `${API}/mtc/certs/${certNo}/pdf?base=${encodeURIComponent(window.location.origin)}`
        + `&t=${encodeURIComponent(t)}${copy ? "&copy=1" : ""}${review ? "&review=1" : ""}`;
      if (w) w.location.replace(url); else window.open(url, "_blank");
    } catch (e) {
      if (w && !w.closed) w.close();
      let detail = e.serverMessage || "";
      try { detail = detail || JSON.parse(await e?.response?.data?.text?.() || "{}").detail || ""; } catch { /* not json */ }
      if (!detail) detail = e?.response?.data?.detail || "";
      if (detail) { e.serverMessage = detail; }
      throw e;
    }
  },
  mtcTagItems: (certNo) => axios.get(`${API}/mtc/certs/${certNo}/tag-items`),
  markingPhotos: (params) => axios.get(`${API}/marking/photos`, { params }),
  addMarkingPhoto: (fd) => axios.post(`${API}/marking/photos`, fd, { timeout: 180000 }),
  markingPhotoUrl: (pid) => `${API}/marking/photos/${pid}/file`,
  markingPhotoBlob: (pid) => axios.get(`${API}/marking/photos/${pid}/file`, { responseType: "blob" }),
  overrideMarkingPhoto: (pid, reason) => axios.post(`${API}/marking/photos/${pid}/override`, { reason }),
  deleteMarkingPhoto: (pid) => axios.delete(`${API}/marking/photos/${pid}`),
  markingGate: (certNo) => axios.get(`${API}/marking/gate/${certNo}`),
  publicMarkingPhotos: (token, code) => axios.get(`${API}/mtc/verify/${token}/marking-photos`, { params: { code } }),
  publicMarkingPhotoUrl: (token, pid, code) => `${API}/mtc/verify/${token}/marking-photos/${pid}/file?code=${encodeURIComponent(code)}`,
  publicMarkingCheck: (token, file, code) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("code", code);
    return axios.post(`${API}/mtc/verify/${token}/marking-check`, fd, { timeout: 180000, headers: { Authorization: "" } });
  },
  mtcTagsZpl: (certNo, items, size, dpi) => axios.get(`${API}/mtc/certs/${certNo}/tags/zpl`,
    { params: { items, size, dpi, base: window.location.origin } }),
  downloadMtcTags: async (certNo, items, size) => {
    const r = await axios.get(`${API}/mtc/certs/${certNo}/tags/pdf`, {
      params: { items, size, base: window.location.origin }, responseType: "blob", timeout: 120000,
    });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${certNo.replace(/\//g, "_")}_tags.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },
  getLetterhead: () => axios.get(`${API}/mtc/letterhead`),
  syncLetterhead: () => axios.post(`${API}/mtc/letterhead/sync`, {}, { timeout: 120000 }),
  openMtcCertLetter: async (certNo, kind, to = {}) => {
    // Same treatment as the certificate: the tab says what it is doing while the letter is being
    // numbered, stamped and signed, and the file streams from the server rather than as a blob.
    const label = kind === "qr" ? "QR clarification letter" : "Authentication letter";
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(`<!doctype html><title>${label} — preparing</title>
        <style>body{margin:0;height:100vh;display:flex;align-items:center;justify-content:center;
        font:14px/1.6 system-ui,sans-serif;background:#1B2A4A;color:#fff}
        .b{text-align:center}.s{width:26px;height:26px;margin:0 auto 14px;border:3px solid rgba(255,255,255,.25);
        border-top-color:#fff;border-radius:50%;animation:r .8s linear infinite}
        @keyframes r{to{transform:rotate(360deg)}}small{opacity:.6}</style>
        <div class=b><div class=s></div><b>Preparing the ${label}</b><br>
        <small id=m>${certNo} — numbering the letter, stamping and signing it</small></div>
        <script>setTimeout(function(){var m=document.getElementById('m');if(m){m.innerHTML='If the letter has saved to your downloads instead of opening here, you can close this tab.';}},18000)<\/script>`);
      w.document.close();
    }
    try {
      const t = (await axios.get(`${API}/mtc/pdf-token`)).data.t;
      const q = new URLSearchParams({
        base: window.location.origin, t,
        to_mode: to.to_mode || "", to_name: to.to_name || "", to_addr: to.to_addr || "", note: to.note || "",
      });
      const url = `${API}/mtc/certs/${certNo}/letters/${kind}/pdf?${q}`;
      if (w) w.location.replace(url); else window.open(url, "_blank");
    } catch (e) {
      if (w && !w.closed) w.close();
      throw new Error(e?.response?.data?.detail || e?.message || "Request failed");
    }
  },
  openMtcComplianceReport: async (certNo) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.get(`${API}/mtc/pdf-token`);
      const url = `${API}/mtc/certs/${certNo}/compliance-report.pdf?t=${encodeURIComponent(data.t)}`;
      if (w) w.location = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); throw e; }
  },
  approveSignature: (id) => axios.post(`${API}/mtc/signatures/${id}/approve`),
  cleanSignaturePhoto: (file) => { const fd = new FormData(); fd.append("file", file); return axios.post(`${API}/mtc/signatures/clean-photo`, fd, { responseType: "blob" }); },
  remoteCleanPhoto: (sid, file) => { const fd = new FormData(); fd.append("file", file); return axios.post(`${API}/mtc/sign-remote/${sid}/clean-photo`, fd, { responseType: "blob" }); },
  remoteSignOpen: (body) => axios.post(`${API}/mtc/signatures/remote-session`, body),
  remoteSignStatus: (sid) => axios.get(`${API}/mtc/signatures/remote-session/${sid}`),
  decideSamplingBasis: (b) => axios.post(`${API}/mtc/sampling-basis/decide`, b),
  mtcRequirements: (b) => axios.post(`${API}/mtc/requirements`, b),
  mtcSpecialSettings: () => axios.get(`${API}/mtc/special-case/settings`),
  saveMtcSpecialSettings: (b) => axios.put(`${API}/mtc/special-case/settings`, b),
  mtcSpecialCases: () => axios.get(`${API}/mtc/special-cases`),
  stdDocs: () => axios.get(`${API}/standards/docs`),
  getSpecimenRules: () => axios.get(`${API}/standards/specimen-rules`),
  putSpecimenRules: (body) => axios.put(`${API}/standards/specimen-rules`, body),
  stdFile: (id) => axios.get(`${API}/standards/docs/${id}/file`, { responseType: "blob" }),
  stdUpload: (fd) => axios.post(`${API}/standards/docs`, fd, { headers: { "Content-Type": "multipart/form-data" } }),
  stdExtract: (id) => axios.post(`${API}/standards/docs/${id}/extract`),
  stdExtractAll: () => axios.post(`${API}/standards/extract-all`),
  stdDeleteDoc: (id) => axios.delete(`${API}/standards/docs/${id}`),
  stdDiffs: (p = {}) => axios.get(`${API}/standards/diffs`, { params: p }),
  stdApplyDiffs: (b) => axios.post(`${API}/standards/diffs/apply`, b),
  stdDismissDiff: (id) => axios.post(`${API}/standards/diffs/${id}/dismiss`),
  stdPoints: (p = {}) => axios.get(`${API}/standards/points`, { params: p }),
  stdVerifyPoints: (b) => axios.post(`${API}/standards/points/verify`, b),
  stdLookup: (b) => axios.post(`${API}/standards/lookup`, b),
  stdCoverage: () => axios.get(`${API}/standards/coverage`),
  stdEditions: () => axios.get(`${API}/standards/editions`),
  stdEditionsCheck: () => axios.post(`${API}/standards/editions/check`),
  stdAcceptEdition: (b) => axios.post(`${API}/standards/editions/accept`, b),
  putOrderSupp: (b) => axios.put(`${API}/mtc/order-supp`, b),
  pendingSamplingBasis: () => axios.get(`${API}/mtc/sampling-basis/pending`),
  getSamplingBasis: (so) => axios.get(`${API}/mtc/sampling-basis`, { params: { so_no: so } }),
  putSamplingBasis: (b) => axios.put(`${API}/mtc/sampling-basis`, b),
  remoteSignInfo: (sid) => axios.get(`${API}/mtc/sign-remote/${sid}`),
  getTpiAgencies: () => axios.get(`${API}/mtc/tpi-agencies`),
  saveTpiAgency: (body) => axios.post(`${API}/mtc/tpi-agencies`, body),
  requestTpiSign: (body) => axios.post(`${API}/mtc/tpi-sign-request`, { ...body, origin: window.location.origin }),
  attachSavedTpi: (body) => axios.post(`${API}/mtc/tpi-attach-saved`, body),
  // Hard Copy Sign-Off — TPI agencies that stamp & sign paper
  getHardCopyPacks: (params = {}) => axios.get(`${API}/mtc/hardcopy/packs`, { params }),
  getHardCopyPack: (pid) => axios.get(`${API}/mtc/hardcopy/packs/${pid}`),
  createHardCopyPack: (body) => axios.post(`${API}/mtc/hardcopy/packs`, body),
  hardCopyHandover: (pid, body) => axios.post(`${API}/mtc/hardcopy/packs/${pid}/handover`, body),
  hardCopyReturnLeg: (pid, body) => axios.post(`${API}/mtc/hardcopy/packs/${pid}/return-leg`, body),
  getQapSignoff: (oid) => axios.get(`${API}/qap/orders/${oid}/signoff`),
  saveQapSignoff: (oid, body) => axios.put(`${API}/qap/orders/${oid}/signoff`, body),
  autofillQapSignoff: (oid) => axios.post(`${API}/qap/orders/${oid}/signoff/autofill`),
  openQapSignoffPack: async (oid, include) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.get(`${API}/mtc/pdf-token`);
      const params = new URLSearchParams({ base: window.location.origin, t: data.t, include: (include || []).join(",") });
      const url = `${API}/qap/orders/${oid}/signoff-pack/pdf?${params.toString()}`;
      if (w) w.location = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); throw e; }
  },
  hardCopyRelease: (pid) => axios.post(`${API}/mtc/hardcopy/packs/${pid}/release`),
  deleteHardCopyPack: (pid) => axios.delete(`${API}/mtc/hardcopy/packs/${pid}`),
  remindHardCopyPack: (pid) => axios.post(`${API}/mtc/hardcopy/packs/${pid}/remind`),
  getHardCopyPending: () => axios.get(`${API}/mtc/hardcopy/pending`),
  getHardCopyConfig: () => axios.get(`${API}/mtc/hardcopy/config`),
  saveHardCopyConfig: (body) => axios.put(`${API}/mtc/hardcopy/config`, body),
  setHardCopyStampReuse: (body) => axios.post(`${API}/mtc/hardcopy/stamp-reuse`, body),
  uploadHardCopyScan: (pid, file, fields = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
    return axios.post(`${API}/mtc/hardcopy/packs/${pid}/scan`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
  },
  saveHardCopyStamp: (blob, agency, inspector) => {
    const fd = new FormData();
    fd.append("file", new File([blob], "stamp.png", { type: "image/png" }));
    fd.append("agency", agency || "");
    fd.append("inspector", inspector || "");
    return axios.post(`${API}/mtc/hardcopy/save-stamp`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
  },
  getHardCopyScanBlob: (docId) => axios.get(`${API}/mtc/hardcopy/docs/${docId}/pdf`, { responseType: "blob", timeout: 120000 }),
  openHardCopyScan: async (docId) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.get(`${API}/mtc/pdf-token`);
      const url = `${API}/mtc/hardcopy/docs/${docId}/pdf?t=${encodeURIComponent(data.t)}`;
      if (w) w.location = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); throw e; }
  },
  openHardCopyPackPdf: async (pid) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.get(`${API}/mtc/pdf-token`);
      const params = new URLSearchParams({ base: window.location.origin, t: data.t });
      const url = `${API}/mtc/hardcopy/packs/${pid}/pdf?${params.toString()}`;
      if (w) w.location = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); throw e; }
  },
  heatFix: (body) => axios.post(`${API}/mtc/heat-fix`, body, { timeout: 90000 }),
  remoteSignSubmit: (sid, blob, mode = "", part = "", finish = false, inspDate = "", meta = null) => { const fd = new FormData(); fd.append("file", new File([blob], "signature.png", { type: "image/png" })); if (mode) fd.append("mode", mode); if (part) fd.append("part", part); if (finish) fd.append("finish", "1"); if (inspDate) fd.append("insp_date", inspDate); if (meta?.font) fd.append("sig_font", meta.font); if (meta?.typed_name) fd.append("typed_name", meta.typed_name); return axios.post(`${API}/mtc/sign-remote/${sid}`, fd); },
  getMtcComplianceReport: (certNo) => axios.get(`${API}/mtc/certs/${certNo}/compliance-report`),
  getTdsGrades: () => axios.get(`${API}/mtc/tds-grades`),
  getTdcSoList: () => axios.get(`${API}/mtc/tdc-so-list`, { timeout: 180000 }),
  getTdcClauses: () => axios.get(`${API}/mtc/tdc-clauses`),
  addTdcClause: (data) => axios.put(`${API}/mtc/tdc-clauses`, data),
  createTdc: (data) => axios.post(`${API}/mtc/tdc`, data),
  getTdcs: () => axios.get(`${API}/mtc/tdc`),
  getTdcBySo: (so) => axios.get(`${API}/mtc/tdc/by-so/${so}`),
  setTdcStatus: (no, status) => axios.put(`${API}/mtc/tdc/${no}/status`, { status }),
  uploadTdcSignedCopy: (no, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/mtc/tdc/${no}/signed-copy`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
  },
  deleteTdc: (no) => axios.delete(`${API}/mtc/tdc/${no}`),
  getTdc: (no) => axios.get(`${API}/mtc/tdc/${no}`),
  reviseTdc: (no, data) => axios.post(`${API}/mtc/tdc/${no}/revise`, data),
  openTdcPdf: async (no, signedCopy = false) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.get(`${API}/mtc/pdf-token`);
      const params = new URLSearchParams({ base: window.location.origin, t: data.t });
      const url = `${API}/mtc/tdc/${no}/${signedCopy ? "signed-copy" : "pdf"}?${params.toString()}`;
      if (w) w.location = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); throw e; }
  },
  bumpTdsRevision: (grade, note) => axios.post(`${API}/mtc/tds-revisions/${grade}`, { note }),
  getTdsRevisionHistory: (grade) => axios.get(`${API}/mtc/tds-revisions/${grade}`),
  saveTdsConfig: (data) => axios.put(`${API}/mtc/tds-config`, data),
  logTds: (data) => axios.post(`${API}/mtc/tds-log`, data),
  getTdsLog: () => axios.get(`${API}/mtc/tds-log`),
  deleteTdsLog: (id) => axios.delete(`${API}/mtc/tds-log/${id}`),
  openTdsPdf: async (grade) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.get(`${API}/mtc/pdf-token`);
      const params = new URLSearchParams({ base: window.location.origin, t: data.t });
      const url = `${API}/mtc/tds/${grade}/pdf?${params.toString()}`;
      if (w) w.location = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); throw e; }
  },
  getTdsPhotos: () => axios.get(`${API}/mtc/tds-photos`),
  uploadTdsPhoto: (key, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/mtc/tds-photos/${key}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
  },
  resetTdsPhoto: (key) => axios.delete(`${API}/mtc/tds-photos/${key}`),
  getSpecimenGrades: () => axios.get(`${API}/mtc/specimen-grades`),
  getSpecimens: () => axios.get(`${API}/mtc/specimens`),
  createSpecimen: (data) => axios.post(`${API}/mtc/specimens`, data),
  getSpecimen: (specNo) => axios.get(`${API}/mtc/specimen/${specNo}`),
  reviseSpecimen: (specNo, data) => axios.put(`${API}/mtc/specimen/${specNo}`, data),
  checkSpecimen: (data) => axios.post(`${API}/mtc/specimens/check`, data),
  getSampleStock: () => axios.get(`${API}/mtc/sample-stock`),
  addSampleStock: (body) => axios.post(`${API}/mtc/sample-stock`, body),
  updateSampleStock: (id, body) => axios.put(`${API}/mtc/sample-stock/${id}`, body),
  deleteSampleStock: (id) => axios.delete(`${API}/mtc/sample-stock/${id}`),
  issueSample: (id, body) => axios.post(`${API}/mtc/sample-stock/${id}/issue`, body),
  receiveSample: (id, qty = 1) => axios.post(`${API}/mtc/sample-stock/${id}/receive`, {}, { params: { qty } }),
  getApprovedSpecimen: (customer) => axios.get(`${API}/mtc/approved-specimen`, { params: { customer } }),
  attestEdition: (spec, note = "") => axios.post(`${API}/standards/editions/attest`, { spec, note }),
  productStds: () => axios.get(`${API}/standards/product-stds`),
  saveProductStd: (body) => axios.post(`${API}/standards/product-stds`, body),
  approveProductStd: (key, note = "") => axios.post(`${API}/standards/product-stds/approve`, { key, note }),
  withdrawProductStd: (key) => axios.post(`${API}/standards/product-stds/${key}/withdraw`),
  draftStandard: (docId) => axios.post(`${API}/standards/docs/${docId}/draft-standard`),
  getStandardDraft: (docId) => axios.get(`${API}/standards/docs/${docId}/draft-standard`),
  sendSpecimen: (specNo, body) => axios.post(`${API}/mtc/specimens/${specNo}/send`, { ...body, base: window.location.origin }, { timeout: 120000 }),
  customerEmail: (name) => axios.get(`${API}/customer-email`, { params: { name } }),
  getSpecimenDefaults: (grade, form) => axios.get(`${API}/mtc/specimen-defaults`, { params: { grade, form } }),
  getSpecimenSourceCerts: (customer = "") => axios.get(`${API}/mtc/specimen-source-certs`, { params: { customer } }),
  specimenCopyFrom: (certNo) => axios.get(`${API}/mtc/specimen-copy-from/${certNo}`),
  setSpecimenStatus: (specNo, data) => axios.put(`${API}/mtc/specimens/${specNo}/status`, data),
  deleteSpecimen: (specNo) => axios.delete(`${API}/mtc/specimens/${specNo}`),
  openSpecimenPdf: async (specNo) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.get(`${API}/mtc/pdf-token`);
      const params = new URLSearchParams({ base: window.location.origin, t: data.t });
      const url = `${API}/mtc/specimens/${specNo}/pdf?${params.toString()}`;
      if (w) w.location = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); throw e; }
  },
  verifyMtc: (token, heat = "") => axios.get(`${API}/mtc/verify/${token}`, { params: heat ? { heat } : {} }),
  naceAdvice: (lines) => axios.post(`${API}/mtc/nace-advice`, { lines }),
  mtcCertLookup: (q) => axios.get(`${API}/mtc/certs/lookup`, { params: { q } }),
  aiExtractHeat: (heat, docId = "") => axios.post(`${API}/mtc/heat-tests/${encodeURIComponent(heat)}/ai-extract`, null, { params: docId ? { doc_id: docId } : {}, timeout: 180000 }),
  getSignatures: () => axios.get(`${API}/mtc/signatures`),
  mtcManualDetail: (body) => axios.post(`${API}/mtc/manual-detail`, body),
  mtcSupplierMtcUpload: (fd) => axios.post(`${API}/mtc/supplier-mtc`, fd, { timeout: 120000 }),
  checkHeatTest: (body) => axios.post(`${API}/mtc/heat-tests/check`, body),
  getStandardsLibrary: () => axios.get(`${API}/mtc/standards-library`),
  mtcFillTypical: (body) => axios.post(`${API}/mtc/heat-tests/fill-typical`, body),
  mtcSupplierMtcExtract: (body) => axios.post(`${API}/mtc/supplier-mtc/extract`, body, { timeout: 240000 }),
  mtcSupplierMtcApply: (body) => axios.post(`${API}/mtc/supplier-mtc/apply`, body),
  mtcRemarkTemplates: () => axios.get(`${API}/mtc/remark-templates`),
  mtcMarkingTemplates: (customer = "") => axios.get(`${API}/mtc/marking-templates`, { params: { customer } }),
  heatChain: (heat) => axios.get(`${API}/mtc/heat-chain/${encodeURIComponent(heat)}`, { timeout: 60000 }),
  getGradeMaster: () => axios.get(`${API}/mtc/grade-master`),
  saveGradeMaster: (body) => axios.put(`${API}/mtc/grade-master`, body),
  prefillGrade: (q) => axios.post(`${API}/mtc/grade-master/ai-prefill`, { q }, { timeout: 90000 }),
  saveMtcMarkingTemplates: (templates) => axios.put(`${API}/mtc/marking-templates`, { templates }),
  composeMtcMarking: (body) => axios.post(`${API}/mtc/marking-compose`, body),
  saveMtcRemarkTemplates: (templates) => axios.put(`${API}/mtc/remark-templates`, { templates }),
  mtcTestDefaults: () => axios.get(`${API}/mtc/test-defaults`),
  mtcTestTexts: (isTube) => axios.get(`${API}/mtc/test-texts`, { params: { is_tube: !!isTube } }),
  saveMtcTestText: (key, payload) => axios.put(`${API}/mtc/test-texts/${key}`, payload),
  resetMtcTestText: (key) => axios.delete(`${API}/mtc/test-texts/${key}`),
  saveMtcTestDefaults: (values, show, families) => axios.put(`${API}/mtc/test-defaults`, { values, show, families }),
  saveSignatureDefaults: (payload) => axios.put(`${API}/mtc/signatures/defaults`, payload),
  uploadSignature: (fd) => axios.post(`${API}/mtc/signatures`, fd, { timeout: 60000 }),
  getSignatureImage: (id) => axios.get(`${API}/mtc/signatures/${id}/image`, { responseType: "blob" }),
  editSignature: (id, body) => axios.put(`${API}/mtc/signatures/${id}`, body),
  replaceSignatureImage: (id, fd) => axios.post(`${API}/mtc/signatures/${id}/image`, fd, { timeout: 60000 }),
  getSignLayout: () => axios.get(`${API}/mtc/sign-layout`),
  saveSignLayout: (payload) => axios.put(`${API}/mtc/sign-layout`, payload),
  previewSignLayout: (payload) => axios.post(`${API}/mtc/sign-layout/preview`, payload, { responseType: "blob" }),
  getPlugLogs: (month = "") => axios.get(`${API}/plugs/log`, { params: month ? { month } : {} }),
  addPlugLog: (payload) => axios.post(`${API}/plugs/log`, payload),
  deletePlugLog: (id) => axios.delete(`${API}/plugs/log/${id}`),
  getPlugSummary: () => axios.get(`${API}/plugs/summary`),
  getPlugPrices: () => axios.get(`${API}/plugs/prices`),
  savePlugPrices: (prices) => axios.put(`${API}/plugs/prices`, { prices }),
  applyPlugCost: (month) => axios.post(`${API}/plugs/apply-to-costsheet`, { month }),
  momMeetings: () => axios.get(`${API}/mom/meetings`),
  momPoints: (mid) => axios.get(`${API}/mom/${mid}`),
  momImport: (file, { commit = false, sheet = "", meeting_date = "", carry_forward = true } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    if (sheet) fd.append("sheet", sheet);
    if (meeting_date) fd.append("meeting_date", meeting_date);
    fd.append("carry_forward", carry_forward ? "1" : "0");
    if (commit) fd.append("commit", "1");
    return axios.post(`${API}/mom/import`, fd, { timeout: 120000 });
  },
  momUpdatePoint: (pid, patch) => axios.put(`${API}/mom/points/${pid}`, patch),
  momPushTask: (pid, due_at = "") => axios.post(`${API}/mom/points/${pid}/push-task`, { due_at }),
  momDeleteMeeting: (mid) => axios.delete(`${API}/mom/${mid}`),
  momAgeing: (mid = "") => axios.get(`${API}/mom/report/ageing`, { params: { mid } }),
  momCompare: (a, b) => axios.get(`${API}/mom/report/compare`, { params: { a, b } }),
  momSendReminders: () => axios.post(`${API}/mom/report/send-reminders`, {}),
  getTaskSettings: () => axios.get(`${API}/tasks/settings`),
  saveTaskSettings: (payload) => axios.put(`${API}/tasks/settings`, payload),
  getTaskEmployees: () => axios.get(`${API}/tasks/employees`),
  saveTaskEmployee: (payload) => axios.post(`${API}/tasks/employees`, payload),
  deleteTaskEmployee: (id) => axios.delete(`${API}/tasks/employees/${id}`),
  getOdooEmployees: (q) => axios.get(`${API}/tasks/odoo-employees`, { params: { q } }),
  getTaskTeams: () => axios.get(`${API}/tasks/teams`),
  saveTaskTeam: (payload) => axios.post(`${API}/tasks/teams`, payload),
  deleteTaskTeam: (id) => axios.delete(`${API}/tasks/teams/${id}`),
  getTasks: (status = "") => axios.get(`${API}/tasks`, { params: status ? { status } : {} }),
  createTask: (payload) => axios.post(`${API}/tasks`, payload),
  setTaskStatus: (id, status) => axios.post(`${API}/tasks/${id}/status`, { status }),
  deleteTask: (id) => axios.delete(`${API}/tasks/${id}`),
  sendDprNow: () => axios.post(`${API}/tasks/send-dpr-now`, {}, { timeout: 60000 }),
  getMtcKpis: () => axios.get(`${API}/mtc/kpis`),
  getMtcCertGaps: (days = 45) => axios.get(`${API}/mtc/cert-gaps`, { params: { days }, timeout: 60000 }),
  getGradeSpecs: () => axios.get(`${API}/mtc/grade-specs`),
  searchHeats: (params) => axios.get(`${API}/mtc/heat-search`, { params }),
  getHeatTimeline: (heat) => axios.get(`${API}/lab/heat-timeline/${encodeURIComponent(heat)}`),
  getLabReports: (params = {}) => axios.get(`${API}/lab/reports`, { params }),
  uploadLabReport: (formData) => axios.post(`${API}/lab/reports`, formData, { timeout: 120000 }),
  downloadLabReport: (id) => axios.get(`${API}/lab/reports/${id}/download`, { responseType: "blob", timeout: 60000 }),
  deleteLabReport: (id) => axios.delete(`${API}/lab/reports/${id}`),
  aiExtractLabReport: (id) => axios.post(`${API}/lab/reports/${id}/ai-extract`, null, { timeout: 180000 }),
  getLabRequests: (params = {}) => axios.get(`${API}/lab/requests`, { params }),
  getMtcLastCertDefaults: (customer) => axios.get(`${API}/mtc/last-cert-defaults`, { params: { customer } }),
  reviewMtcCert: (body) => axios.post(`${API}/mtc/review`, body),
  mtcDimsCheck: (body) => axios.post(`${API}/mtc/dims-check`, body),
  previewMtcPdf: (body) => axios.post(`${API}/mtc/preview-pdf`, body, { responseType: "blob", timeout: 90000 }),
  getLabRegister: () => axios.get(`${API}/lab/register`),
  saveLabRegister: (labs) => axios.put(`${API}/lab/register`, { labs }),
  labRequestPdf: (id) => axios.get(`${API}/lab/requests/${id}/pdf`, { responseType: "blob" }),
  refreshBundleRoute: (bid) => axios.post(`${API}/bundles/${bid}/refresh-route`),
  dailyRates: () => axios.get(`${API}/daily-rates`),
  dailyRatesSave: (p) => axios.post(`${API}/daily-rates`, p),
  dailyRatesShortlist: (grades) => axios.put(`${API}/daily-rates/shortlist`, { grades }),
  dailyRateDraftAct: (id, action, p = {}) => axios.post(`${API}/daily-rates/drafts/${id}/${action}`, p),
  dailyIssueTargets: (product = "pipes", currency = "INR") => axios.get(`${API}/daily-rates/issue-targets`, { params: { product, currency } }),
  dailyIssueBatch: (p) => axios.post(`${API}/daily-rates/issue-batch`, p, { timeout: 180000 }),
  getPriceMaster: () => axios.get(`${API}/price-master`),
  savePriceMaster: (m) => axios.put(`${API}/price-master`, m),
  setPriceIndices: (p) => axios.post(`${API}/price-master/indices`, p),
  fetchPriceIndices: () => axios.post(`${API}/price-master/indices/fetch`),
  pmRecompute: (p) => axios.post(`${API}/price-master/recompute`, p),
  pmBulkAdjust: (p) => axios.post(`${API}/price-master/bulk-adjust`, p),
  getPriceMasterList: (audience, customer, product = "pipes", currency = "INR", partner_id = 0) => axios.get(`${API}/price-master/list`, { params: { audience, customer, product, currency, partner_id } }),
  pmIssue: (p) => axios.post(`${API}/price-master/issue`, p),
  pmPreflight: (p) => axios.post(`${API}/price-master/issue/preflight`, p, { timeout: 120000 }),
  pmSignatories: () => axios.get(`${API}/price-master/signatories`),
  pmSignatoriesSave: (p) => axios.put(`${API}/price-master/signatories`, p),
  pmAiFetchIndices: () => axios.post(`${API}/price-master/indices/ai-fetch`, {}, { timeout: 120000 }),
  pmMetalLoss: (audience) => axios.get(`${API}/price-master/metal-loss`, { params: { audience: audience || "customer" } }),
  pmAudienceCompare: () => axios.get(`${API}/price-master/audience-compare`),
  rmSup: () => axios.get(`${API}/rm-suppliers`),
  rmSupCfg: (c) => axios.put(`${API}/rm-suppliers/config`, c),
  rmSupBoard: () => axios.get(`${API}/rm-suppliers/board`),
  rmSupQuote: (q) => axios.post(`${API}/rm-suppliers/quotes`, q),
  rmSupDelQuote: (id) => axios.delete(`${API}/rm-suppliers/quotes/${id}`),
  rmSupHistory: (grade, days = 180) => axios.get(`${API}/rm-suppliers/history`, { params: { grade, days } }),
  rmSupUse: (p) => axios.post(`${API}/rm-suppliers/use`, p),
  rmSupSyncOdoo: () => axios.post(`${API}/rm-suppliers/sync-odoo`, {}),
  rmSupAutoRun: () => axios.post(`${API}/rm-suppliers/auto-run`, {}),
  pmMarketWatch: () => axios.get(`${API}/price-master/market-watch`),
  pmMarketWatchSave: (p) => axios.put(`${API}/price-master/market-watch`, p),
  pmMarketWatchRun: () => axios.post(`${API}/price-master/market-watch/run`, {}, { timeout: 120000 }),
  pmAiAdvise: () => axios.post(`${API}/price-master/ai-advise`),
  pmExport: (audience, customer, fmt, product = "pipes", currency = "INR", partner_id = 0, view = "", grade = "", nbs = "", schs = "", unit = "kg", grades = "", validity = "", remarks = "", opts = {}) => axios.get(`${API}/price-master/export`, { params: { audience, customer, fmt, product, currency, partner_id, view, grade, nbs, schs, unit, grades, validity, remarks, ...opts }, responseType: "blob" }),
  getCosting: () => axios.get(`${API}/costing`),
  saveCosting: (sheet) => axios.put(`${API}/costing`, sheet),
  computeCosting: (grade, od, route = "auto", wt = null) => axios.get(`${API}/costing/compute`, { params: { grade, od, route, ...(wt ? { wt } : {}) } }),
  yieldCheck: (days = 90) => axios.get(`${API}/costing/yield-check`, { params: { days } }),
  costingActualYields: (days = 90) => axios.get(`${API}/costing/actual-yields`, { params: { days }, timeout: 180000 }),
  archLearnRoutes: (refresh = false) => axios.get(`${API}/odoo-architecture/learn-routes`, { params: { refresh }, timeout: 180000 }),
  recoveryCheck: (days = 90) => axios.get(`${API}/costing/recovery-check`, { params: { days } }),
  contractorRecon: (month) => axios.get(`${API}/contractors/recon-summary`, { params: { month } }),
  costingAiReview: () => axios.post(`${API}/costing/ai-review`),
  costingAiEstimate: (fd) => axios.post(`${API}/costing/ai-estimate`, fd, { timeout: 180000 }),
  costingPush: (apply) => axios.post(`${API}/costing/push-to-master`, { apply }),
  costingPmDrift: () => axios.get(`${API}/costing/pm-drift`),
  costingSizeSplit: () => axios.get(`${API}/costing/size-split`),
  costingExport: (grade, od, route, fmt, wt = null) => axios.get(`${API}/costing/export`, { params: { grade, od, route, fmt, ...(wt ? { wt } : {}) }, responseType: "blob" }),
  costingRevisions: () => axios.get(`${API}/costing/revisions`),
  costingRevisionCreate: (note) => axios.post(`${API}/costing/revisions`, { note }),
  costingRevisionCompare: (a, b) => axios.get(`${API}/costing/revisions/compare`, { params: { a, b } }),
  pmTemplates: () => axios.get(`${API}/price-master/templates`),
  pmTemplateSave: (name) => axios.post(`${API}/price-master/templates`, { name }),
  pmTemplateApply: (tid) => axios.post(`${API}/price-master/templates/${tid}/apply`),
  pmTemplateDelete: (tid) => axios.delete(`${API}/price-master/templates/${tid}`),
  pmGradeSuggest: (grade) => axios.get(`${API}/price-master/grade-suggest`, { params: { grade } }),
  pmVsMarket: (weeks = 26, min_sales = 1, refresh = false) => axios.get(`${API}/price-master/vs-market`, { params: { weeks, min_sales, refresh } }),
  pmIssues: (customer = "", archived = 0) => axios.get(`${API}/price-master/issues`, { params: { customer, archived } }),
  sendCtx: (issueId = "") => axios.get(`${API}/pricelist-send/context`, { params: { issue_id: issueId } }),
  sendCustomers: (q) => axios.get(`${API}/pricelist-send/customers`, { params: { q } }),
  sendPricelist: (p) => axios.post(`${API}/pricelist-send`, p, { timeout: 300000 }),
  sendLog: (issueNo = "") => axios.get(`${API}/pricelist-send/log`, { params: { issue_no: issueNo } }),
  sendRetry: (batch) => axios.post(`${API}/pricelist-send/retry/${batch}`, {}, { timeout: 300000 }),
  sendCancelJob: (jid) => axios.delete(`${API}/pricelist-send/jobs/${jid}`),
  sendSaveList: (label, targets) => axios.post(`${API}/pricelist-send/lists`, { label, targets }),
  sendDeleteList: (lid) => axios.delete(`${API}/pricelist-send/lists/${lid}`),
  pmIssuesBulk: (action, ids) => axios.post(`${API}/price-master/issues/bulk`, { action, ids }),
  pmPublished: () => axios.get(`${API}/price-master/published`),
  pmPublishPreview: (iid) => axios.get(`${API}/price-master/issues/${iid}/publish-preview`),
  pmAssignGroups: (iid, groupIds) => axios.post(`${API}/price-master/issues/${iid}/assign-groups`, { group_ids: groupIds }),
  pmPublish: (iid, groups = [], validUntil = "", effectiveFrom = "", notify = false, notifyNote = "") => axios.post(`${API}/price-master/issues/${iid}/publish`, { groups, valid_until: validUntil, effective_from: effectiveFrom, notify, notify_note: notifyNote }),
  waCreateRequiredTemplates: () => axios.post(`${API}/whatsapp/templates/create-required`),
  pmUnpublish: (iid) => axios.delete(`${API}/price-master/issues/${iid}/publish`),
  pmIssueExport: (iid, fmt, view = "", grade = "", opts = {}) => axios.get(`${API}/price-master/issues/${iid}/export`, { params: { fmt, view, grade, ...opts }, responseType: "blob" }),
  pmLog: () => axios.get(`${API}/price-master/log`),
  pmHistory: () => axios.get(`${API}/price-master/history`),
  createLabRequest: (body) => axios.post(`${API}/lab/requests`, body),
  setLabRequestStatus: (id, action) => axios.post(`${API}/lab/requests/${id}/status`, { action }),
  submitLabResults: (id, body) => axios.post(`${API}/lab/requests/${id}/results`, body),
  getInstruments: () => axios.get(`${API}/lab/instruments`),
  createInstrument: (body) => axios.post(`${API}/lab/instruments`, body),
  updateInstrument: (id, body) => axios.put(`${API}/lab/instruments/${id}`, body),
  deleteInstrument: (id) => axios.delete(`${API}/lab/instruments/${id}`),
  uploadInstrumentCert: (id, fd) => axios.post(`${API}/lab/instruments/${id}/cert`, fd, { timeout: 120000 }),
  downloadInstrumentCert: (id) => axios.get(`${API}/lab/instruments/${id}/cert/download`, { responseType: "blob", timeout: 60000 }),
  getInstrumentSummary: () => axios.get(`${API}/lab/instruments/summary`),
  saveBundlePmi: (bid, body) => axios.post(`${API}/bundles/${encodeURIComponent(bid)}/pmi`, body),
  clearBundlePmi: (bid, note) => axios.post(`${API}/bundles/${encodeURIComponent(bid)}/pmi-clear`, { note }),
  getPmiSummary: () => axios.get(`${API}/bundles/pmi-summary`),
  getPmiDigest: (date = "") => axios.get(`${API}/bundles/pmi-digest`, { params: date ? { date } : {} }),
  getReprintAlerts: () => axios.get(`${API}/bundles/reprint-alerts`),
  getPrintLog: (params = {}) => axios.get(`${API}/bundles/print-log`, { params }),
  getPmiStickersPdf: (params) => axios.get(`${API}/bundles/pmi-stickers/pdf`, { params: { ...params, base: window.location.origin }, responseType: "blob", timeout: 120000 }),
  getPmiStickersZpl: (params) => axios.get(`${API}/bundles/pmi-stickers/zpl`, { params: { ...params, base: window.location.origin } }),
  getBundleLabelsZpl: (ids, size, dpi) => axios.get(`${API}/bundles/labels/zpl`, { params: { ids: ids.join(","), base: window.location.origin, size, dpi } }),
  getContractors: () => axios.get(`${API}/contractors`),
  createContractor: (body) => axios.post(`${API}/contractors`, body),
  updateContractor: (id, body) => axios.put(`${API}/contractors/${id}`, body),
  deleteContractor: (id) => axios.delete(`${API}/contractors/${id}`),
  getContractorProduction: (month) => axios.get(`${API}/contractors/production`, { params: { month } }),
  getContractorBill: (id, month) => axios.get(`${API}/contractors/${id}/bill-full`, { params: { month } }),
  getContractorBillPdf: (id, month) => axios.get(`${API}/contractors/${id}/bill/pdf`, { params: { month }, responseType: "blob", timeout: 120000 }),
  getContractorRateStatement: (id) => axios.get(`${API}/contractors/${id}/rate-statement`, { responseType: "blob", timeout: 60000 }),
  getContractorDeductions: (id, month) => axios.get(`${API}/contractors/${id}/deductions`, { params: { month } }),
  addContractorDeduction: (id, body) => axios.post(`${API}/contractors/${id}/deductions`, body),
  deleteContractorDeduction: (dedId) => axios.delete(`${API}/contractors/deductions/${dedId}`),
  getWorkDaySheets: (date) => axios.get(`${API}/contractors/entries`, { params: { date } }),
  reviewWorkEntries: (body) => axios.post(`${API}/contractors/entries/review`, body),
  getWorkDayReport: (date, fmt) => axios.get(`${API}/contractors/entries/report`, { params: { date, fmt }, responseType: "blob", timeout: 120000 }),
  getWorkVariance: (month) => axios.get(`${API}/contractors/entries/variance`, { params: { month } }),
  getRejectTrend: (months = 6) => axios.get(`${API}/contractors/entries/reject-trend`, { params: { months } }),
  getWorkConfig: () => axios.get(`${API}/contractors/work-config`),
  setWorkConfig: (body) => axios.patch(`${API}/contractors/work-config`, body),
  deleteWorkEntry: (eid) => axios.delete(`${API}/contractors/entries/${eid}`),
  getContractorPins: (cid) => axios.get(`${API}/contractors/${cid}/pins`),
  addContractorPin: (cid, body) => axios.post(`${API}/contractors/${cid}/pins`, body),
  toggleContractorPin: (pid, active) => axios.patch(`${API}/contractors/pins/${pid}`, { active }),
  getContractorEntriesBill: (cid, month) => axios.get(`${API}/contractors/${cid}/entries-bill`, { params: { month } }),
  ignoreMtcGap: (pickingId, reason) => axios.post(`${API}/mtc/cert-gaps/${pickingId}/ignore`, { reason }),
  getMtcSkipRules: () => axios.get(`${API}/mtc/skip-rules`),
  saveMtcSkipRules: (p) => axios.put(`${API}/mtc/skip-rules`, p),
  deleteSignature: (id) => axios.delete(`${API}/mtc/signatures/${id}`),
  getShiftDispatch: () => axios.get(`${API}/weekplan/shift-dispatch`, { timeout: 60000 }),
  saveBundleHrb: (bid, hrb) => axios.post(`${API}/bundles/${encodeURIComponent(bid)}/hrb`, { hrb }),
  setHrbCutoff: (cutoff) => axios.post(`${API}/bundles/hrb-cutoff`, { cutoff }),
  getDies: () => axios.get(`${API}/dies`),
  createDie: (payload) => axios.post(`${API}/dies`, payload),
  logDieUsage: (id, payload) => axios.post(`${API}/dies/${id}/log`, payload),
  setDieStatus: (id, payload) => axios.post(`${API}/dies/${id}/status`, payload),
  getYieldAnalytics: (days = 180, refresh = false) => axios.get(`${API}/weekplan/yield`, { params: { days, refresh }, timeout: 280000 }),
  getJobCard: (q) => axios.get(`${API}/jobs/card`, { params: { q }, timeout: 180000 }),
  getJobCardPdf: (ref, base) => axios.get(`${API}/jobs/card/pdf`, { params: { ref, base }, responseType: "blob", timeout: 180000 }),
  // Process Cards (CTP / PIR / PLP)
  pcTemplates: () => axios.get(`${API}/process-cards/templates`),
  pcConfig: () => axios.get(`${API}/process-cards/config`),
  pcConfigSave: (body) => axios.put(`${API}/process-cards/config`, body),
  pcPrefill: (type, mo_id) => axios.get(`${API}/process-cards/prefill`, { params: { type, mo_id }, timeout: 180000 }),
  pcCreate: (body) => axios.post(`${API}/process-cards`, body, { timeout: 180000 }),
  pcList: (params = {}) => axios.get(`${API}/process-cards`, { params }),
  pcGet: (id) => axios.get(`${API}/process-cards/${id}`),
  pcUpdate: (id, body) => axios.put(`${API}/process-cards/${id}`, body),
  pcApprove: (id) => axios.post(`${API}/process-cards/${id}/approve`),
  pcCancel: (id, note = "") => axios.post(`${API}/process-cards/${id}/cancel`, { note }),
  pcRevise: (id, note = "") => axios.post(`${API}/process-cards/${id}/revise`, { note }),
  pcPdf: (id, layout) => axios.get(`${API}/process-cards/${id}/pdf`, { params: { layout, base: window.location.origin }, responseType: "blob", timeout: 120000 }),
  pcLib: (type = "") => axios.get(`${API}/process-cards/lib/list`, { params: { type } }),
  pcLibUpdate: (id, lib) => axios.put(`${API}/process-cards/lib/${encodeURIComponent(id)}`, { lib }),
  pcLibDelete: (id) => axios.delete(`${API}/process-cards/lib/${encodeURIComponent(id)}`),
  pcCtpBars: (ref, lot = 0) => axios.get(`${API}/process-cards/ctp-bars`, { params: { ref, lot } }),
  pcReleased: (dept) => axios.get(`${API}/process-cards/released`, { params: { dept } }),
  pcAck: (id, dept) => axios.post(`${API}/process-cards/${id}/ack`, { dept }),
  pcPirSources: (params) => axios.get(`${API}/process-cards/pir-sources`, { params }),
  getClientCodes: () => axios.get(`${API}/jobs/client-codes`),
  saveClientCodes: (codes) => axios.post(`${API}/jobs/client-codes`, { codes }),
  weighBundle: (bid, kg) => axios.post(`${API}/bundles/${encodeURIComponent(bid)}/weigh`, { kg }),
  savePieceRules: (payload) => axios.post(`${API}/bundles/piece-rules`, payload),
  getJobsEta: (refresh = false) => axios.get(`${API}/jobs/eta${refresh ? "?refresh=true" : ""}`, { timeout: 280000 }),
  getStuckDays: () => axios.get(`${API}/jobs/stuck-days`),
  setStuckDays: (stuck_days) => axios.post(`${API}/jobs/stuck-days`, { stuck_days }),
  getBundles: (params = {}) => axios.get(`${API}/bundles`, { params }),
  getBundleMoPicker: (q = "") => axios.get(`${API}/bundles/mo-picker`, { params: { q } }),
  getBundleMoBackfill: () => axios.get(`${API}/bundles/mo-backfill`, { timeout: 120000 }),
  attachBundleMo: (bid, body) => axios.post(`${API}/bundles/${encodeURIComponent(bid)}/attach-mo`, body),
  getBundleNextMo: (bid, stage) => axios.get(`${API}/bundles/${encodeURIComponent(bid)}/next-mo`, { params: { stage }, timeout: 45000 }),
  getIncomingInbox: () => axios.get(`${API}/incoming/inbox`, { timeout: 90000 }),
  getIncoming: (heat) => axios.get(`${API}/incoming/${encodeURIComponent(heat)}`),
  parseIncomingTc: (fd) => axios.post(`${API}/incoming/parse-tc`, fd, { timeout: 180000 }),
  saveIncomingInspection: (heat, body) => axios.post(`${API}/incoming/${encodeURIComponent(heat)}/inspect`, body, { timeout: 120000 }),
  getIncomingConfig: () => axios.get(`${API}/incoming/config/get`),
  setIncomingConfig: (body) => axios.post(`${API}/incoming/config/set`, body),
  getBundle: (bid) => axios.get(`${API}/bundles/${encodeURIComponent(bid)}`),
  createBundles: (payload) => axios.post(`${API}/bundles`, payload),
  moveBundle: (bid, stage, note = "", operator = "") => axios.post(`${API}/bundles/${encodeURIComponent(bid)}/move`, { stage, note, operator }),
  undoBundleMove: (bid) => axios.post(`${API}/bundles/${encodeURIComponent(bid)}/undo-move`),
  deleteBundle: (bid) => axios.delete(`${API}/bundles/${encodeURIComponent(bid)}`),
  getBundleLabelsPdf: (ids, base, size = "a5") => axios.get(`${API}/bundles/labels/pdf`, { params: { ids: ids.join(","), base, size }, responseType: "blob", timeout: 120000 }),
  getBundleStages: () => axios.get(`${API}/bundles/stages`),
  // Go-Live prep
  getGoliveChecklist: () => axios.get(`${API}/golive/checklist`),
  toggleGoliveItem: (id) => axios.post(`${API}/golive/checklist/toggle`, { id }),
  addGoliveItem: (text) => axios.post(`${API}/golive/checklist/add`, { text }),
  removeGoliveItem: (id) => axios.post(`${API}/golive/checklist/remove`, { id }),
  createOpeningWip: (rows) => axios.post(`${API}/golive/opening-wip`, { rows }),
  getGoliveFloorRefs: () => axios.get(`${API}/golive/floor-refs`),
  getSopPosterPdf: (dept = "") => axios.get(`${API}/golive/sop/pdf`, { params: { dept, base: window.location.origin }, responseType: "blob", timeout: 120000 }),
  // Handover (custody handshake)
  getHandoverDepartments: () => axios.get(`${API}/handover/departments`),
  getWorkspaceOverview: (dept = "") => axios.get(`${API}/workspace/overview`, { params: dept ? { dept } : {} }),
  getWorkspacePlanning: (due_window = "14d") => axios.get(`${API}/workspace/planning`, { params: { due_window } }),
  getDailyOps: (date = "") => axios.get(`${API}/daily-ops`, { params: date ? { date } : {} }),
  confirmDailyOps: (payload) => axios.post(`${API}/daily-ops/confirm`, payload),
  unconfirmDailyOps: (date) => axios.post(`${API}/daily-ops/unconfirm`, { date }),
  getDailyOpsSummary: (days = 7) => axios.get(`${API}/daily-ops/summary`, { params: { days } }),
  getInspectionOffers: (params = {}) => axios.get(`${API}/inspection-offers`, { params }),
  createInspectionOffer: (payload) => axios.post(`${API}/inspection-offers`, payload),
  updateInspectionOfferStatus: (oid, payload) => axios.post(`${API}/inspection-offers/${encodeURIComponent(oid)}/status`, payload),
  lookupInspectionMo: (mo) => axios.get(`${API}/inspection-offers/mo/${encodeURIComponent(mo)}`),
  importInspectionOffers: (formData) => axios.post(`${API}/inspection-offers/import`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  getInspectionBacklogSummary: () => axios.get(`${API}/inspection-offers/backlog-summary`),
  exportInvoiceExcel: (dateFrom, dateTo) =>
    axios.get(`${API}/dispatch/export-invoices`, { params: { date_from: dateFrom, date_to: dateTo }, responseType: "blob" }),
  setDispatchLotSource: (lot, source) => axios.put(`${API}/dispatch/lot-source`, { lot, source }),
  getHandoverBalances: () => axios.get(`${API}/handover/balances`),
  getHandoverPending: (params = {}) => axios.get(`${API}/handover/pending`, { params }),
  getHandoverLog: (params = {}) => axios.get(`${API}/handover/log`, { params }),
  getHandoverForBundle: (bid) => axios.get(`${API}/handover/for-bundle/${encodeURIComponent(bid)}`),
  sendHandover: (payload) => axios.post(`${API}/handover/send`, payload),
  acceptHandover: (hid, lines, note = "") => axios.post(`${API}/handover/${encodeURIComponent(hid)}/accept`, { lines, note }),
  cancelHandover: (hid) => axios.post(`${API}/handover/${encodeURIComponent(hid)}/cancel`),
  getHandoverDisputes: (params = {}) => axios.get(`${API}/handover/disputes`, { params }),
  resolveHandoverDispute: (did, payload) => axios.post(`${API}/handover/disputes/${encodeURIComponent(did)}/resolve`, payload),
  getHandoverDaily: (date) => axios.get(`${API}/handover/daily`, { params: { date } }),
  getWipRegister: () => axios.get(`${API}/handover/wip-register`),
  getBrokerQuotes: (status) => axios.get(`${API}/broker-quotes`, { params: { status } }),
  getBrokerAgentStats: () => axios.get(`${API}/broker-quotes/agent-stats`),
  setBrokerQuoteStatus: (id, payload) => axios.post(`${API}/broker-quotes/${id}/status`, payload),
  checkBrokerQuote: (id) => axios.post(`${API}/broker-quotes/${id}/check`),
  brokerQuoteProforma: (id, payload) => axios.post(`${API}/broker-quotes/${id}/proforma`, payload, { responseType: "blob", timeout: 60000 }),
  getWipRegisterExport: (fmt, by) => axios.get(`${API}/handover/wip-register/${fmt}`, { params: { by }, responseType: "blob", timeout: 60000 }),
  getHandoverClose: (month) => axios.get(`${API}/handover/close/${month}`),
  saveCloseCount: (month, payload) => axios.post(`${API}/handover/close/${month}/count`, payload),
  signClose: (month, dept) => axios.post(`${API}/handover/close/${month}/sign`, { dept }),
  lockClose: (month) => axios.post(`${API}/handover/close/${month}/lock`),
  getSyncConfig: () => axios.get(`${API}/odoo-sync/config`),
  setSyncMode: (mode, wo_live_wcs) => axios.post(`${API}/odoo-sync/config`, wo_live_wcs === undefined ? { mode } : { mode, wo_live_wcs }),
  getWoReview: (params) => axios.get(`${API}/wo-post/review`, { params, timeout: 180000 }),
  getWoStages: () => axios.get(`${API}/wo-post/stages`),
  getWcHealth: () => axios.get(`${API}/wo-post/wc-health`, { timeout: 60000 }),
  approveWo: (payload) => axios.post(`${API}/wo-post/approve`, payload, { timeout: 180000 }),
  manualWoEvent: (payload) => axios.post(`${API}/wo-post/manual`, payload),
  dismissWoEvent: (id) => axios.delete(`${API}/wo-post/event/${id}`),
  getShiftConfig: () => axios.get(`${API}/settings/shift-config`),
  saveShiftConfig: (payload) => axios.put(`${API}/settings/shift-config`, payload),
  getSyncQueue: (params = {}) => axios.get(`${API}/odoo-sync/queue`, { params }),
  reverseSyncJob: (jid, reason) => axios.post(`${API}/odoo-sync/queue/${jid}/reverse`, { reason }),
  getReconDay: (date) => axios.get(`${API}/recon/day`, { params: { date }, timeout: 120000 }),
  getReconInbox: (state = "open") => axios.get(`${API}/recon/inbox`, { params: { state } }),
  reconAction: (id, action, note) => axios.post(`${API}/recon/exceptions/${encodeURIComponent(id)}/${action}`, { note }),
  getSyncMoCandidates: (jid, q = "") => axios.get(`${API}/odoo-sync/queue/${jid}/mo-candidates`, { params: { q }, timeout: 60000 }),
  pickSyncMo: (jid, mo_id) => axios.post(`${API}/odoo-sync/queue/${jid}/pick-mo`, { mo_id }),
  setReconConfig: (live_stages) => axios.put(`${API}/recon/config`, { live_stages }),
  retrySyncJob: (jid) => axios.post(`${API}/odoo-sync/retry/${jid}`),
  searchOdooProducts: (q) => axios.get(`${API}/odoo-sync/product-search`, { params: { q }, timeout: 60000 }),
  resolveSyncJob: (jid, payload) => axios.post(`${API}/odoo-sync/resolve/${jid}`, payload),
  getGapReport: (params = {}) => axios.get(`${API}/odoo-sync/gap-report`, { params, timeout: 180000 }),
  getBundleJobDefaults: (ref) => axios.get(`${API}/bundles/job-defaults`, { params: { ref }, timeout: 180000 }),
  removeBundlePieces: (bid, payload) => axios.post(`${API}/bundles/${encodeURIComponent(bid)}/remove-pieces`, payload),
  getLoosePieces: () => axios.get(`${API}/bundles/loose`),
  scrapLoosePiece: (pid, note = "") => axios.post(`${API}/bundles/loose/${pid}/scrap`, { note }),
  returnLoosePiece: (pid, stage) => axios.post(`${API}/bundles/loose/${pid}/return`, { stage }),
  combineLoosePieces: (piece_ids, stage, desc = "", heat = "") => axios.post(`${API}/bundles/combine`, { piece_ids, stage, desc, heat }),
  applyReservationActions: (items) => axios.post(`${API}/planning/reservations/apply`, { items }, { timeout: 180000 }),
  getReservationAudit: (limit = 50) => axios.get(`${API}/planning/reservations/audit?limit=${limit}`),
  setPriorityCustomers: (customers) => axios.put(`${API}/settings/priority-customers`, { customers }),
  saveFilterView: (page, name, state) => axios.post(`${API}/settings/filter-views`, { page, name, state }),
  setDefaultFilterView: (page, name) => axios.post(`${API}/settings/filter-views/default`, { page, name }),
  deleteFilterView: (page, name) => axios.delete(`${API}/settings/filter-views?page=${page}&name=${encodeURIComponent(name)}`),
  savePromiseRevision: (so, product, newDue) => axios.post(`${API}/sales/promise-revisions`, { so, product, new_due: newDue }),
  getTopSellers: (months = 6, refresh = false) => axios.get(`${API}/offers/top-sellers?months=${months}${refresh ? "&refresh=true" : ""}`, { timeout: 180000 }),
  getReorderFollowups: () => axios.get(`${API}/offers/reorder-followups`),
  markReorderFollowup: (customer) => axios.post(`${API}/offers/reorder-followups`, { customer }),
  unmarkReorderFollowup: (customer) => axios.delete(`${API}/offers/reorder-followups?customer=${encodeURIComponent(customer)}`),
  aiReorderPitch: (customer, items, refresh = false) => axios.post(`${API}/ai/reorder-pitch${refresh ? "?refresh=true" : ""}`, { customer, items }, { timeout: 120000 }),
  exportTopSellers: (months, by) => axios.get(`${API}/offers/top-sellers/export?months=${months}&by=${by}`, { responseType: "blob", timeout: 180000 }),
  exportDeadStock: (days, scope, extra = {}) => axios.get(`${API}/offers/dead-stock/export`, { params: { min_days: days, scope, ...extra }, responseType: "blob", timeout: 180000 }),
  getStockTags: () => axios.get(`${API}/offers/stock-tags`),
  addStockTag: (payload) => axios.post(`${API}/offers/stock-tags`, payload),
  deleteStockTag: (id) => axios.delete(`${API}/offers/stock-tags/${id}`),
  getOfferRounds: () => axios.get(`${API}/offers/rounds`),
  createOfferRound: (payload) => axios.post(`${API}/offers/rounds`, payload),
  getOfferRound: (rid) => axios.get(`${API}/offers/rounds/${rid}`),
  updateOfferRound: (rid, payload) => axios.put(`${API}/offers/rounds/${rid}`, payload),
  deleteOfferRound: (rid) => axios.delete(`${API}/offers/rounds/${rid}`),
  bulkMarkSold: (rid, itemIds, broker, price) => axios.put(`${API}/offers/rounds/${rid}/items/bulk-sold`, { item_ids: itemIds, broker, price: price || null }),
  addOfferQuote: (rid, payload) => axios.post(`${API}/offers/rounds/${rid}/quotes`, payload),
  deleteOfferQuote: (rid, qid) => axios.delete(`${API}/offers/rounds/${rid}/quotes/${qid}`),
  markOfferSold: (rid, itemId, payload) => axios.put(`${API}/offers/rounds/${rid}/items/${itemId}/sold`, payload),
  exportOfferRound: (rid, fmt) => axios.get(`${API}/offers/rounds/${rid}/export?fmt=${fmt}`, { responseType: "blob" }),
  getPriceList: (weeks = 6, refresh = false) => axios.get(`${API}/offers/price-list?weeks=${weeks}${refresh ? "&refresh=true" : ""}`),
  adjustPriceList: (payload) => axios.post(`${API}/offers/price-list/adjust`, payload),
  adjustDeadStockPrices: (payload) => axios.post(`${API}/offers/dead-stock/liquidation/adjust`, payload),
  exportPriceList: (weeks, uplift, fmt, extra = {}) => axios.get(`${API}/offers/price-list/export`, { params: { weeks, uplift, fmt, ...extra }, responseType: "blob" }),
  getBrokers: () => axios.get(`${API}/offers/brokers`),
  // Directors view
  getDirectorsOverview: (refresh = false, range = {}) => axios.get(`${API}/directors/overview`, { params: { refresh, ...range } }),
  getDirectorsShopfloor: (range = {}) => axios.get(`${API}/directors/shopfloor`, { params: range }),
  getYieldScrap: (range = {}) => axios.get(`${API}/directors/yield-scrap`, { params: range }),
  getBottleneck: (range = {}) => axios.get(`${API}/directors/bottleneck`, { params: range }),
  getStageDetail: (key, range = {}) => axios.get(`${API}/directors/stage/${encodeURIComponent(key)}`, { params: range }),
  bulkDirectorIssues: (ids, action, value) => axios.post(`${API}/directors/issues/bulk`, { ids, action, value }),
  getCapacity: () => axios.get(`${API}/directors/capacity`),
  saveCapacity: (lines) => axios.put(`${API}/directors/capacity`, { lines }),
  getCashflowFixed: () => axios.get(`${API}/directors/cashflow-fixed`),
  saveCashflowFixed: (items) => axios.put(`${API}/directors/cashflow-fixed`, { items }),
  getDirectorsBrief: (refresh = false) => axios.get(`${API}/directors/brief`, { params: { refresh } }),
  getDirectorIssues: () => axios.get(`${API}/directors/issues`),
  createDirectorIssue: (payload) => axios.post(`${API}/directors/issues`, payload),
  updateDirectorIssue: (id, payload) => axios.put(`${API}/directors/issues/${id}`, payload),
  deleteDirectorIssue: (id) => axios.delete(`${API}/directors/issues/${id}`),
  getDirectorAssignees: () => axios.get(`${API}/directors/assignees`),
  getDirectorsCashflow: (refresh = false, months = 3) => axios.get(`${API}/directors/cashflow`, { params: { refresh, months } }),
  getPriceChangeLog: () => axios.get(`${API}/offers/price-list/change-log`),
  getMyActions: () => axios.get(`${API}/my/actions`),
  updateMyAction: (id, payload) => axios.put(`${API}/my/actions/${id}`, payload),
  getBrokerQuoteHistory: () => axios.get(`${API}/offers/brokers/quote-history`),
  getTablePrefs: (key) => axios.get(`${API}/settings/table-prefs`, { params: { key } }),
  saveTablePrefs: (key, cols, known) => axios.put(`${API}/settings/table-prefs`, { key, cols, known: known || [] }),
  addBroker: (payload) => axios.post(`${API}/offers/brokers`, payload),
  updateBroker: (id, payload) => axios.put(`${API}/offers/brokers/${id}`, payload),
  deleteBroker: (id) => axios.delete(`${API}/offers/brokers/${id}`),
  setOfferRecipients: (rid, broker_ids) => axios.put(`${API}/offers/rounds/${rid}/recipients`, { broker_ids }),
  getOfferRoundFollowUps: (rid) => axios.get(`${API}/offers/rounds/${rid}/follow-ups`),
  stageLotsExport: (process, stage) => axios.get(`${API}/stock/stage-flow/lots-export?process=${encodeURIComponent(process)}&stage=${encodeURIComponent(stage)}`, { responseType: "blob" }),
  compareOfferRound: (rid) => axios.get(`${API}/offers/rounds/${rid}/compare`),
  getOfferPriceHistory: (rid) => axios.get(`${API}/offers/rounds/${rid}/price-history`),
  quotesCustomers: (q) => axios.get(`${API}/quotes/customers?q=${encodeURIComponent(q)}`),
  quotesProducts: (q, f = {}) => axios.get(`${API}/quotes/product-search?q=${encodeURIComponent(q)}&nb=${encodeURIComponent(f.nb || "")}&sch=${encodeURIComponent(f.sch || "")}&od=${encodeURIComponent(f.od || "")}&wt=${encodeURIComponent(f.wt || "")}`),
  quotesQuickProducts: () => axios.get(`${API}/quotes/quick-products`),
  quoteWhatsApp: (qid, mobile) => axios.post(`${API}/quotes/${qid}/whatsapp`, { mobile }),
  getQuoteFilterPresets: () => axios.get(`${API}/quotes/filter-presets`),
  saveQuoteFilterPreset: (payload) => axios.post(`${API}/quotes/filter-presets`, payload),
  deleteQuoteFilterPreset: (id) => axios.delete(`${API}/quotes/filter-presets/${id}`),
  quotesLineCheck: (payload) => axios.post(`${API}/quotes/line-check`, payload),
  quoteDeviations: (days = 60) => axios.get(`${API}/quotes/deviations`, { params: { days } }),
  quotesCustomerHistory: (pid) => axios.get(`${API}/quotes/customer-history/${pid}`),
  aiParseInquiry: (text) => axios.post(`${API}/ai/parse-inquiry`, { text }, { timeout: 120000 }),
  aiParseInquiryFiles: (fd) => axios.post(`${API}/ai/parse-inquiry-files`, fd, { timeout: 180000 }),
  catalogPrefs: () => axios.get(`${API}/catalog/prefs`),
  saveCatalogPrefs: (p) => axios.put(`${API}/catalog/prefs`, p),
  catalogStageMismatches: (refresh) => axios.get(`${API}/catalog/stage-mismatches`, { params: refresh ? { refresh: true } : {}, timeout: 120000 }),
  aiIntakeSettings: () => axios.get(`${API}/ai-intake/settings`),
  saveAiIntakeSettings: (model_id) => axios.put(`${API}/ai-intake/settings`, { model_id }),
  getInquiries: () => axios.get(`${API}/inquiries`),
  setInquiryStatus: (id, status) => axios.post(`${API}/inquiries/${id}/status`, { status }),
  deleteInquiry: (id) => axios.delete(`${API}/inquiries/${id}`),
  getMailSettings: () => axios.get(`${API}/inquiries/mail-settings`),
  saveMailSettings: (payload) => axios.put(`${API}/inquiries/mail-settings`, payload),
  mailTest: () => axios.post(`${API}/inquiries/mail-test`),
  mailSendTest: (to) => axios.post(`${API}/inquiries/mail-send-test`, { to }, { timeout: 90000 }),
  mailPollNow: () => axios.post(`${API}/inquiries/mail-poll`, {}, { timeout: 180000 }),
  getQuotes: () => axios.get(`${API}/quotes`),
  getQuote: (id) => axios.get(`${API}/quotes/${id}`),
  createQuote: (payload) => axios.post(`${API}/quotes`, payload),
  updateQuote: (id, payload) => axios.put(`${API}/quotes/${id}`, payload),
  deleteQuoteDoc: (id) => axios.delete(`${API}/quotes/${id}`),
  exportQuote: (id, fmt) => axios.get(`${API}/quotes/${id}/export?fmt=${fmt}`, { responseType: "blob" }),
  soApprovals: () => axios.get(`${API}/quotes-so-approvals`),
  decideSoApproval: (aid, action, note = "") => axios.post(`${API}/quotes-so-approvals/${aid}`, { action, note }),
  pushQuoteToOdoo: (id) => axios.post(`${API}/quotes/${id}/push-to-odoo`),
  quotesPriceApprovals: () => axios.get(`${API}/quotes/price-approvals`),
  decideQuotePriceApproval: (qid, p) => axios.post(`${API}/quotes/price-approvals/${qid}`, p),
  quoteFromPriceList: (p) => axios.post(`${API}/quotes/from-price-list`, p),
  getQuoteTermsDefaults: () => axios.get(`${API}/quotes/terms-defaults`),
  getQuoteTermsTemplates: () => axios.get(`${API}/quotes/terms-templates`),
  saveQuoteTermsTemplate: (p) => axios.post(`${API}/quotes/terms-templates`, p),
  deleteQuoteTermsTemplate: (tid) => axios.delete(`${API}/quotes/terms-templates/${tid}`),
  getQuoteFollowUps: () => axios.get(`${API}/quotes/follow-ups`),
  createQuoteShare: (qid, payload) => axios.post(`${API}/quotes/${qid}/share`, payload),
  accessQuoteShare: (token, password) => axios.post(`${API}/quotes/shared/${token}`, { password }),
  issuePriceList: (payload) => axios.post(`${API}/offers/price-list/issue`, payload),
  getPriceListIssues: () => axios.get(`${API}/offers/price-list/issues`),
  exportPriceListIssue: (iid, fmt) => axios.get(`${API}/offers/price-list/issues/${iid}/export?fmt=${fmt}`, { responseType: "blob" }),
  deletePriceListIssue: (iid) => axios.delete(`${API}/offers/price-list/issues/${iid}`),
  comparePriceListIssue: (iid) => axios.get(`${API}/offers/price-list/issues/${iid}/compare`),
  aiQuoteAdvise: (qid, refresh = false) => axios.post(`${API}/ai/quote/${qid}/advise?refresh=${refresh}`, {}, { timeout: 120000 }),
  aiQuoteFollowUp: (qid, refresh = false) => axios.post(`${API}/ai/quote/${qid}/follow-up-draft?refresh=${refresh}`, {}, { timeout: 120000 }),
  aiRoundAnalyze: (rid, refresh = false) => axios.post(`${API}/ai/offers/round/${rid}/analyze?refresh=${refresh}`, {}, { timeout: 120000 }),
  aiBrokerNudge: (rid, bid, refresh = false) => axios.post(`${API}/ai/offers/round/${rid}/nudge/${bid}?refresh=${refresh}`, {}, { timeout: 120000 }),
  aiPriceListCommentary: (refresh = false) => axios.post(`${API}/ai/offers/price-list/commentary?refresh=${refresh}`, {}, { timeout: 120000 }),
  aiWinLossInsights: (refresh = false) => axios.post(`${API}/ai/quotes/win-loss-insights?refresh=${refresh}`, {}, { timeout: 120000 }),
  getPlanningViews: () => axios.get(`${API}/planning/views`),
  savePlanningView: (payload) => axios.post(`${API}/planning/views`, payload),
  deletePlanningView: (name) => axios.delete(`${API}/planning/views/${encodeURIComponent(name)}`),
  setDefaultPlanningView: (name) => axios.post(`${API}/planning/views/default`, { name }),
  getSalesPriceAnalysis: (params) => axios.get(`${API}/sales/price-analysis`, { params, timeout: 120000 }),
  getSalesMargin: (params) => axios.get(`${API}/sales/margin`, { params, timeout: 180000 }),
  getJwProcesses: () => axios.get(`${API}/sales/margin/jw-processes`),
  saveJwOverride: (so, payload) => axios.put(`${API}/sales/margin/jw-override/${so}`, payload),
  deleteJwOverride: (so) => axios.delete(`${API}/sales/margin/jw-override/${so}`),
  getNavLayout: () => axios.get(`${API}/nav/layout`),
  saveNavLayout: (payload) => axios.post(`${API}/settings/nav-layout`, payload),
  resetNavLayout: () => axios.delete(`${API}/settings/nav-layout`),
  saveNavPins: (pins) => axios.post(`${API}/nav/pins`, { pins }),
  getNotesLedger: (params) => axios.get(`${API}/notes/ledger`, { params, timeout: 120000 }),
  saveNoteFollowup: (id, payload) => axios.post(`${API}/notes/ledger/${id}/followup`, payload),
  getMarginConfig: () => axios.get(`${API}/settings/margin-config`),
  saveMarginConfig: (payload) => axios.post(`${API}/settings/margin-config`, payload),
  getVendorScorecard: (params) => axios.get(`${API}/purchase/vendor-scorecard`, { params, timeout: 120000 }),
  getPurchasePriceAnalysis: (params) => axios.get(`${API}/purchase/price-analysis`, { params, timeout: 120000 }),
  getConsumablesPrice: (params) => axios.get(`${API}/purchase/consumables-price`, { params, timeout: 120000 }),
  getPurchaseAnalytics: (refresh = false, from, to) => axios.get(`${API}/purchase/analytics`, { params: { refresh: refresh || undefined, from, to } }),
  getPurchaseBudgets: () => axios.get(`${API}/purchase/budgets`),
  savePurchaseBudgets: (budgets) => axios.put(`${API}/purchase/budgets`, { budgets }),
  getBudgetAlerts: () => axios.get(`${API}/purchase/budget-alerts`),
  spendExport: () => axios.get(`${API}/purchase/spend-export`, { responseType: "blob" }),
  getDateProposals: () => axios.get(`${API}/planning/date-proposals`),
  saveDateProposal: (payload) => axios.post(`${API}/planning/date-proposals`, payload),
  setDateProposalStatus: (payload) => axios.post(`${API}/planning/date-proposals/status`, payload),
  getDeliveryRisk: (refresh = false, includeClosed = false, microPct = 2) => {
    const p = new URLSearchParams();
    if (refresh) p.append("refresh", "true");
    if (includeClosed) p.append("include_closed", "true");
    p.append("micro_pct", String(microPct));
    return axios.get(`${API}/dashboard/delivery-risk?${p.toString()}`);
  },
  getPlanningBuffers: () => axios.get(`${API}/planning/buffers`),
  putPlanningBuffers: (payload) => axios.put(`${API}/planning/buffers`, payload),
  // Nav settings
  getNavSettings: () => axios.get(`${API}/settings/nav`),
  saveNavSettings: (tabs) => axios.post(`${API}/settings/nav`, { tabs }),
  // Odoo credentials (admin)
  getOdooSettings: () => axios.get(`${API}/settings/odoo`),
  updateOdooApiKey: (api_key) => axios.post(`${API}/settings/odoo`, { api_key }),
  switchOdooEnv: (payload) => axios.post(`${API}/settings/odoo/env`, payload, { timeout: 60000 }),
  odooEnv: () => axios.get(`${API}/odoo-env`),
  // Cache management (admin)
  getCacheStatus: () => axios.get(`${API}/admin/cache-status`),
  triggerCacheCheck: () => axios.post(`${API}/admin/cache-check`),
  clearCache: () => axios.post(`${API}/admin/cache-clear`),
  // Saved Views (per-user, per-page filter presets)
  listSavedViews: (pageKey) => axios.get(`${API}/saved-views/${pageKey}`),
  createSavedView: (pageKey, body) => axios.post(`${API}/saved-views/${pageKey}`, body),
  deleteSavedView: (pageKey, viewId) => axios.delete(`${API}/saved-views/${pageKey}/${viewId}`),
  setDefaultView: (pageKey, viewId) => axios.post(`${API}/saved-views/${pageKey}/${viewId}/default`),
  // Reconciliation (Odoo Docs spreadsheet vs live Odoo stock)
  listReconcileSheets: () => axios.get(`${API}/reconcile/sheets`),
  reconcileDiff: (body) => axios.post(`${API}/reconcile/diff`, body),
  reconcileStart: (body) => axios.post(`${API}/reconcile/start`, body),
  reconcileStatus: (jobId) => axios.get(`${API}/reconcile/status/${jobId}`),
  reconcileExport: (body) => axios.post(`${API}/reconcile/export`, body, { responseType: "arraybuffer" }),
  // Build version (open endpoint — no auth required)
  getVersion: () => axios.get(`${API}/version`),
  // Role-based access control
  getRoles: () => axios.get(`${API}/settings/roles`),
  saveRolePermissions: (roles) => axios.post(`${API}/settings/role-permissions`, { roles }),
  setUserRole: (username, role) => axios.post(`${API}/settings/user-role/${encodeURIComponent(username)}`, { role }),
  setUserHome: (username, route) => axios.post(`${API}/settings/user-home/${encodeURIComponent(username)}`, { route }),
  setUserRoles: (username, roles) => axios.post(`${API}/settings/user-role/${encodeURIComponent(username)}`, { roles }),
  getMyPermissions: () => axios.get(`${API}/auth/permissions`),
  // Odoo access diagnostic (multi-company/branch visibility)
  odooAccessCheck: () => axios.get(`${API}/admin/odoo-access`),
  odooProductSearch: (q) => axios.get(`${API}/admin/odoo-product-search`, { params: { q } }),
  odooDuplicateVariants: () => axios.get(`${API}/admin/odoo-duplicate-variants`),
  odooDuplicateResolve: (display_name, action = "resolve") =>
    axios.post(`${API}/admin/odoo-duplicate-variants/resolve`, { display_name, action }),
  // Branding (admin)
  getBranding: () => axios.get(`${API}/settings/branding`),
  updateBranding: (data) => axios.post(`${API}/settings/branding`, data),
  uploadLogo: (file) => { const fd = new FormData(); fd.append("file", file); return axios.post(`${API}/settings/branding/logo`, fd); },
  uploadFavicon: (file) => { const fd = new FormData(); fd.append("file", file); return axios.post(`${API}/settings/branding/favicon`, fd); },
  // Document (PDF) branding (admin)
  getDocBranding: () => axios.get(`${API}/settings/doc-branding`),
  getMtcMasters: () => axios.get(`${API}/settings/mtc-masters`),
  verifyMtcBranding: () => axios.get(`${API}/mtc/verify-branding`),
  getMtcCertScans: (certNo) => axios.get(`${API}/mtc/certs/${certNo}/scans`),
  getMtcCertPrintLog: (certNo) => axios.get(`${API}/mtc/certs/${certNo}/print-log`),
  getSuppTestRules: () => axios.get(`${API}/mtc/supp-test-rules`),
  getG48Defaults: (grade = "", spec = "") => axios.get(`${API}/mtc/g48-defaults`, { params: { grade, spec } }),
  checkG48: (p) => axios.post(`${API}/mtc/g48-check`, p),
  saveSuppTestRules: (p) => axios.put(`${API}/mtc/supp-test-rules`, p),
  getSpecDeviations: (days = 90) => axios.get(`${API}/mtc/spec-deviations?days=${days}`),
  labOcr: (file) => { const fd = new FormData(); fd.append("file", file); return axios.post(`${API}/mtc/lab-ocr`, fd, { timeout: 180000 }); },
  saveMtcMasters: (data) => axios.put(`${API}/settings/mtc-masters`, data),
  saveDocBranding: (data) => axios.put(`${API}/settings/doc-branding`, data),
  aiSuggestDocBranding: () => axios.post(`${API}/settings/doc-branding/ai-suggest`),
  previewDocBranding: (data) => axios.get(`${API}/settings/doc-branding/preview`, { params: data, responseType: "blob" }),
  getDocBrandingPresets: () => axios.get(`${API}/settings/doc-branding/presets`),
  saveDocBrandingPreset: (data) => axios.post(`${API}/settings/doc-branding/presets`, data),
  deleteDocBrandingPreset: (id) => axios.delete(`${API}/settings/doc-branding/presets/${id}`),
  applyDocBrandingPreset: (id) => axios.post(`${API}/settings/doc-branding/presets/${id}/apply`),
  // User activity (admin)
  getUserActivity: () => axios.get(`${API}/settings/users`),
  getAccessList: () => axios.get(`${API}/settings/access`),
  setAccess: (username, body) => axios.post(`${API}/settings/access/${encodeURIComponent(username)}`, body),
  getActivityLog: (limit = 100, userFilter = null) => {
    const p = new URLSearchParams({ limit });
    if (userFilter) p.append("user_filter", userFilter);
    return axios.get(`${API}/activity-log?${p}`);
  },
  // Dispatch Report (new pivot)
  getDispatchReport: (dateFrom, dateTo, granularity = "monthly", refresh = false) => {
    const p = new URLSearchParams({ granularity });
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/dispatch/report?${p}`);
  },
  // Sales Orders
  getSalesOrders: (status = "open", refresh = false) => {
    const p = new URLSearchParams({ status });
    if (refresh) p.append("refresh", "true");
    return axios.get(`${API}/sales/orders?${p}`);
  },
  createSOShare: (payload) => axios.post(`${API}/sales/share`, payload),
  accessSOShare: (token, password) => axios.post(`${API}/sales/shared/${token}`, { password }),
  // Approvals & Audit Packs
  getApprovalsMaster: () => axios.get(`${API}/approvals/master`),
  saveApprovalsMaster: (data) => axios.post(`${API}/approvals/master`, data),
  pullMasterFromOdoo: () => axios.post(`${API}/approvals/master/pull-odoo`, null, { timeout: 120000 }),
  importSubmissionPack: (formData) => axios.post(`${API}/approvals/master/import-doc`, formData, { timeout: 420000 }),
  getImportJob: (id) => axios.get(`${API}/approvals/master/import-jobs/${id}`),
  vaultAiExtract: (id) => axios.post(`${API}/approvals/vault/${id}/ai-extract`, null, { timeout: 180000 }),
  getVaultDocs: () => axios.get(`${API}/approvals/vault`),
  uploadVaultDoc: (formData) => axios.post(`${API}/approvals/vault`, formData, { timeout: 120000 }),
  getVaultFile: (id) => axios.get(`${API}/approvals/vault/${id}/file`, { responseType: "blob" }),
  deleteVaultDoc: (id) => axios.delete(`${API}/approvals/vault/${id}`),
  buildSubmissionPack: (data) => axios.post(`${API}/approvals/packs/build`, data, { responseType: "blob", timeout: 180000 }),
  getPackTemplates: () => axios.get(`${API}/approvals/packs/templates`),
  savePackTemplate: (data) => axios.post(`${API}/approvals/packs/templates`, data),
  deletePackTemplate: (id) => axios.delete(`${API}/approvals/packs/templates/${id}`),
  getQuestionnaires: () => axios.get(`${API}/approvals/questionnaires`),
  getQuestionnaire: (id) => axios.get(`${API}/approvals/questionnaires/${id}`),
  uploadQuestionnaire: (formData) => axios.post(`${API}/approvals/questionnaires`, formData, { timeout: 300000 }),
  updateQuestionnaire: (id, data) => axios.put(`${API}/approvals/questionnaires/${id}`, data),
  deleteQuestionnaire: (id) => axios.delete(`${API}/approvals/questionnaires/${id}`),
  exportQuestionnaire: (id) => axios.get(`${API}/approvals/questionnaires/${id}/export`, { responseType: "blob", timeout: 120000 }),
  // MTC clauses + customer PDF profiles
  getMtcClauses: () => axios.get(`${API}/settings/mtc-clauses`),
  saveMtcClauses: (data) => axios.put(`${API}/settings/mtc-clauses`, data),
  getCustomerProfiles: () => axios.get(`${API}/mtc/customer-profiles`),
  saveCustomerProfile: (data) => axios.post(`${API}/mtc/customer-profiles`, data),
  deleteCustomerProfile: (id) => axios.delete(`${API}/mtc/customer-profiles/${id}`),
  // VQAS — Visual Quality Acceptance Standard
  vqasCatalog: () => axios.get(`${API}/vqas/catalog`),
  vqasCreateEntry: (d) => axios.post(`${API}/vqas/catalog`, d),
  vqasUpdateEntry: (id, d) => axios.patch(`${API}/vqas/catalog/${id}`, d),
  vqasDeleteEntry: (id) => axios.delete(`${API}/vqas/catalog/${id}`),
  vqasMediaStart: (d) => axios.post(`${API}/vqas/media/start`, d),
  vqasMediaChunk: (fd) => axios.post(`${API}/vqas/media/chunk`, fd, { timeout: 120000 }),
  vqasMediaComplete: (d) => axios.post(`${API}/vqas/media/complete`, d, { timeout: 300000 }),
  vqasDeleteMedia: (id) => axios.delete(`${API}/vqas/media/${id}`),
  vqasTolerances: () => axios.get(`${API}/vqas/tolerances`),
  vqasSaveTolerances: (d) => axios.put(`${API}/vqas/tolerances`, d),
  vqasReferee: (d) => axios.post(`${API}/vqas/referee`, d),
  vqasDisputes: () => axios.get(`${API}/vqas/disputes`),
  vqasCreateDispute: (d) => axios.post(`${API}/vqas/disputes`, d),
  vqasUpdateDispute: (id, d) => axios.patch(`${API}/vqas/disputes/${id}`, d),
  vqasDisputeReferee: (id, d) => axios.post(`${API}/vqas/disputes/${id}/referee`, d),
  vqasSignoffs: () => axios.get(`${API}/vqas/signoffs`),
  searchVendors: (q) => axios.get(`${API}/contractors/vendors`, { params: { q } }),
  // Odoo Architecture Map (Settings)
  archGet: () => axios.get(`${API}/odoo-architecture`),
  archSave: (map) => axios.put(`${API}/odoo-architecture`, { map }),
  archReset: () => axios.post(`${API}/odoo-architecture/reset`),
  archParse: (name, categ) => axios.get(`${API}/odoo-architecture/parse`, { params: { name, categ: categ || "" } }),
  archValidateStart: () => axios.post(`${API}/odoo-architecture/validate`),
  archValidateGet: (group) => axios.get(`${API}/odoo-architecture/validate`, { params: group ? { group } : {} }),
  archValidateXlsx: () => axios.get(`${API}/odoo-architecture/validate.xlsx`, { responseType: "blob" }),
  // Odoo reference write-back (Route Card / Bundle Nos Studio fields)
  refsStatus: () => axios.get(`${API}/odoo-refs/status`),
  refsPreview: () => axios.get(`${API}/odoo-refs/preview`, { timeout: 120000 }),
  refsPush: () => axios.post(`${API}/odoo-refs/push`),
  refsConfig: (payload) => axios.post(`${API}/odoo-refs/config`, payload),
};
